import { localEventDate, eventInstant, viewerToday } from '../../shared/eventTimezones';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FlagIcon from '@mui/icons-material/Flag';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { supabase } from '../config/supabase';
import { effectiveHistoricalLineup, loadHistoricalPlayedEvents } from '../utils/historicalLineup';
import type { AvailabilityStatus, LookupState, TimeFormat, UserPreferences } from '../types';
import { comparePlayerNames, formatPlayerName } from '../utils/playerPreferences';
import {
  buildPlanningRecommendations,
  dismissedPlanningRecommendationKeysForUser,
  visiblePlanningRecommendations,
  type PlanningRecommendation,
} from '../utils/planningRecommendations';
import { loadParticipationReportRows, type ParticipationReportRow } from '../utils/participationReport';
import { teamAccentColor } from '../utils/teamColors';
import { formatTime } from '../utils/timeFormat';
import { normalizeCompatibility, normalizePairingRule, pairingRuleLabels, partnerPreferenceForPair, type PartnerCompatibility, type PartnerPairingRule } from '../utils/partnerPreferences';
import { buildPlanningBoardCompetitivenessIndicator, type PlanningBoardEdrIndicator } from '../utils/planningBoardEdr';
import { buildPlanningBoardOpenSlotsIndicator, type PlanningBoardOpenSlotsIndicator } from '../utils/planningBoardOpenSlots';
import { buildThreeDayLineupWarnings, type LineupLoadEntry, type LineupLoadMatch, type ThreeDayLineupWarning } from '../utils/planningBoardLineupLoad';
import { buildPlanningIntelligence, type PlanningIntelligenceAvailability, type PlanningIntelligenceAssignment, type PlanningIntelligenceMatch, type PlanningIntelligenceRoster } from '../utils/planningIntelligence';
import { playersRosteredOnAllSelectedTeams, playersRosteredOnAnySelectedTeam } from '../utils/planningBoardPlayerFilter';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import {
  availabilityStatusReviewLabel,
  latestAvailabilityChangeByCell,
  loadUnreviewedAvailabilityChanges,
  markAvailabilityChangesReviewed,
  type AvailabilityChangeAttention,
} from '../utils/availabilityAttention';
import { groupPlayerViewDates, playerViewStatus, playerViewCoverage, playerViewMatchesForPlayers } from '../utils/planningBoardPlayerView';
import { PlayerParticipationDetailsDialog } from './PlayerParticipationDetailsDialog';
import { LineupBuilder } from './LineupBuilder';
import { PlayerDisplayControls } from './PlayerDisplayControls';
import { SeasonTeamMultiSelect } from './SeasonTeamMultiSelect';
import { accessibleTeamSelection, restoreAccessibleTeamSelection, sameTeamSelection } from '../utils/captainWorkflowScope';

type PlanningBoardProps = {
  lookups: LookupState;
  defaultCaptainTeamIds: string[];
  userPreferences: UserPreferences;
  onUserPreferencesChange: (patch: Partial<UserPreferences>) => void;
  timeFormat: TimeFormat;
  onOpenPlayerSchedule: (playerId: string) => void;
  currentUserId: string | null;
  canRefreshGroupMeEvents?: boolean;
  onOpenAvailability?: (context: { teamId?: string | null; matchId?: string | null; returnTarget?: 'planning-board' }) => void;
  onOpenMatchDetail?: (matchId: string) => void;
  onOpenLineup?: (matchId: string, context?: { teamId?: string | null }) => void;
  onAvailabilityChangesReviewed?: () => void;
  isFullScreen?: boolean;
  onFullScreenChange?: (next: boolean) => void;
};

type TeamOption = {
  id: string;
  team_name: string;
  short_name?: string | null;
  season_label: string | null;
  workspace_id: string | null;
  team_color: string | null;
  player_rating_basis: string | null;
  enforce_max_sit: boolean;
  max_weeks_without_match: number;
};
type MatchRow = {
  timezone?: string | null;
  id: string;
  team_id: string;
  league_configuration_id: string;
  match_date: string;
  match_time: string | null;
  warmup_time: string | null;
  match_site_type: string | null;
  opponent_name: string | null;
  opponent_average_edr: number | null;
  opponent_rating_basis: string | null;
  opponent_subjective_difficulty: number | null;
  match_status: string;
  lineup_status: string | null;
  groupme_event_id: string | null;
};
type RosterRow = {
  team_id: string;
  player_id: string;
  target_match_count: number | null;
  player_tier: string | null;
  roster_status: string | null;
  roster_role: string | null;
  is_active: boolean;
  team_priority: 'primary' | 'normal' | 'secondary' | null;
};
type PlayerRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  sex: 'male' | 'female' | null;
  estimated_dynamic_rating: number | null;
  back_to_back_match_day_tolerance: 'no' | 'if_needed' | 'yes' | null;
  same_day_match_tolerance: 'no' | 'if_needed' | 'yes' | null;
  minimum_recovery_hours: number | null;
};
type PositionRow = {
  id: string;
  league_configuration_id: string;
  position_code: string;
  line_type: string;
  display_order: number;
  required_player_count: number;
  required_male_count: number;
  required_female_count: number;
};
type LineupEntryRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  slot_number: number;
  player_id: string;
};
type StandbyRow = {
  match_event_id: string;
  player_id: string;
};
type AvailabilityRow = {
  match_event_id: string;
  player_id: string;
  availability_status: AvailabilityStatus;
};
type LineStatusRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  line_status: 'normal' | 'default_by_opponent' | 'default_by_us';
};
type ParticipationEventRow = {
  match_event_id: string;
  player_id: string;
  league_configuration_position_id: string | null;
  participation_event_type: string;
  allocation_count_indicator: boolean;
  participation_event_reason: string | null;
};
type PartnerPreferenceRow = {
  id: string;
  workspace_id: string;
  team_id: string | null;
  player1_id: string;
  player2_id: string;
  compatibility: PartnerCompatibility | null;
  pairing_rule: PartnerPairingRule | null;
  is_active?: boolean | null;
  notes: string | null;
};

type GroupMeRefreshResult = {
  status?: string;
  reason?: string;
  kind?: string;
  id?: string;
};

type GroupMeRefreshResponse = {
  results?: GroupMeRefreshResult[];
  error?: string;
};
type BoardPlayerRow = {
  id: string;
  teamId: string;
  seasonId: string;
  playerId: string;
  playerName: string;
  firstName: string;
  lastName: string;
  playerSex: 'male' | 'female' | null;
  totalPlayed: number;
  countedPlayed: number;
  provisionalPlayed: number;
  singlesPlayed: number;
  doublesPlayed: number;
  lateCancels: number;
  lateSubstitutes: number;
  futureSingles: number;
  futureDoubles: number;
  statusDelta: number;
  participationTarget?: number;
  lastPlayedWeeksAgo: number | null;
  nextMatchWeeksAway: number | null;
  homeMatches: number;
  awayMatches: number;
  neutralMatches: number;
  unknownSiteMatches: number;
  totalCompletedAndFutureScheduledMatches: number;
  homeMatchPercentage: number | null;
};
type BoardCell = {
  availability?: AvailabilityStatus;
  text: string;
  tooltip: string;
  conflict?: string;
  matchBelongsToTeam: boolean;
  attention?: AvailabilityChangeAttention | null;
  lineupLoadWarning?: ThreeDayLineupWarning | null;
  maybeAssignment?: boolean;
};
type MatchColumn = {
  key: string;
  match_date: string;
};
type SummaryColumnKey = 'played' | 'future' | 'lateCancels' | 'lateSubs' | 'status';
type SortField = 'playerName' | 'status';
type SortDirection = 'asc' | 'desc';
type PlayerViewMode = 'all' | 'shared' | 'player' | 'matrix';
type PlanningOptionValue = 'showCompletedMatches' | 'showParticipationInfo' | 'showSubstitutePlayers' | 'groupByGender';
type PlanningBoardViewState = {
  teamIds: string[];
  teamScopeMode?: 'default' | 'explicit';
  includeCompleted: boolean;
  includeSubstitutes: boolean;
  showParticipationInfo: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  fullScreen: boolean;
  headerOpen: boolean;
  availabilityChangesOpen: boolean;
  recommendationsOpen: boolean;
  selectedPlayerId: string;
  playerViewMode: PlayerViewMode;
  dateFrom?: string;
  dateTo?: string;
};

function normalizeSortField(value: string | undefined): SortField {
  return value === 'status' ? 'status' : 'playerName';
}

const availabilityStyles: Record<AvailabilityStatus, { bg: string; border: string; text: string; label: string }> = {
  available: { bg: '#dff3e3', border: '#7fbd8a', text: '#174c22', label: 'Available' },
  maybe: { bg: '#dff1fb', border: '#78b9dc', text: '#164864', label: 'Maybe' },
  unknown: { bg: '#eeeeee', border: '#bdbdbd', text: '#424242', label: 'Unknown' },
  not_available: { bg: '#fde1df', border: '#e58b84', text: '#7b1f1a', label: 'Not Available' },
};

const playerViewBadge = (label: string) => ({
  display: 'inline-block', borderRadius: 0.75, px: 0.55, py: 0.15,
  fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.25,
  bgcolor: label === 'Planned' ? '#d8efdd' : label === 'Available' ? '#dcecff' : label === 'Maybe' ? '#fff0c2' : '#e9edf0',
  color: label === 'Planned' ? '#225b32' : label === 'Available' ? '#0955a5' : label === 'Maybe' ? '#704c00' : '#414b54',
});

const accentColumnWidth = 6;
const summaryColumnWidth = 78;
const planningOptionOrder: PlanningOptionValue[] = [
  'showCompletedMatches',
  'showParticipationInfo',
  'showSubstitutePlayers',
  'groupByGender',
];
const planningOptionLabels: Record<PlanningOptionValue, string> = {
  showCompletedMatches: 'Show Completed Matches',
  showParticipationInfo: 'Show Participation Info',
  showSubstitutePlayers: 'Show Substitute Players',
  groupByGender: 'Group by Gender',
};

function formatDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function arrivalTime(match: Pick<MatchRow, 'warmup_time' | 'match_time'>): string | null {
  return match.warmup_time || match.match_time;
}


function scheduledTimesOverlap(first: MatchRow, second: MatchRow): boolean {
  const firstStart = eventInstant(first.match_date, arrivalTime(first), first.timezone)?.epochMilliseconds;
  const secondStart = eventInstant(second.match_date, arrivalTime(second), second.timezone)?.epochMilliseconds;
  if (firstStart == null || secondStart == null) return false;
  const estimatedMatchMinutes = 120 * 60000;
  return firstStart < secondStart + estimatedMatchMinutes && secondStart < firstStart + estimatedMatchMinutes;
}

function matchDateTimeLabel(match: MatchRow, timeFormat: TimeFormat): string {
  const time = formatTime(arrivalTime(match), timeFormat, match);
  return `${formatDate(localEventDate(match))}${time ? ` @ ${time}` : ''}`;
}

function matchShortLabel(match: MatchRow, timeFormat: TimeFormat): string {
  const time = formatTime(arrivalTime(match), timeFormat, match);
  return `${time || 'Time TBD'} vs ${match.opponent_name || 'TBD'}`;
}

function siteTypeLabel(value: string | null): string {
  if (value === 'Home' || value === 'Away' || value === 'Neutral') return value;
  return value || 'Site TBD';
}

function statusLabel(delta: number): string {
  if (delta < 0) return `Low (${delta})`;
  if (delta > 0) return `High (+${delta})`;
  return 'On Target';
}

function statusColor(delta: number): 'primary' | 'success' | 'error' {
  if (delta < 0) return 'error';
  if (delta > 0) return 'primary';
  return 'success';
}

function defaultedLineLabel(status: string | undefined): string {
  if (status === 'default_by_opponent') return 'Default by Opponent';
  if (status === 'default_by_us') return 'Default by Us';
  return '';
}

function CellCornerIndicator({ corner, color, label }: { corner: 'left' | 'right'; color: string; label: string }) {
  return (
    <Tooltip title={label} arrow>
      <Box
        component="span"
        role="img"
        aria-label={label}
        tabIndex={0}
        sx={{
          position: 'absolute',
          top: 0,
          [corner]: 0,
          width: 0,
          height: 0,
          borderTop: `12px solid ${color}`,
          borderLeft: corner === 'right' ? '12px solid transparent' : undefined,
          borderRight: corner === 'left' ? '12px solid transparent' : undefined,
          outline: 'none',
          '&:focus-visible': {
            filter: 'drop-shadow(0 0 0.2rem rgba(27,94,32,0.8))',
          },
        }}
      />
    </Tooltip>
  );
}

function sexInitial(sex: string | null | undefined): string {
  if (sex === 'male') return 'M';
  if (sex === 'female') return 'F';
  return '';
}

export function PlanningBoard({
  lookups,
  defaultCaptainTeamIds,
  userPreferences,
  onUserPreferencesChange,
  timeFormat,
  onOpenPlayerSchedule,
  currentUserId,
  canRefreshGroupMeEvents = false,
  onOpenAvailability,
  onOpenMatchDetail,
  onOpenLineup,
  onAvailabilityChangesReviewed,
  isFullScreen = false,
  onFullScreenChange,
}: PlanningBoardProps) {
  const defaultViewState = useMemo<PlanningBoardViewState>(() => ({
    teamIds: defaultCaptainTeamIds,
    teamScopeMode: 'default',
    includeCompleted: false,
    includeSubstitutes: false,
    showParticipationInfo: false,
    sortField: 'playerName',
    sortDirection: 'asc',
    fullScreen: false,
    headerOpen: true,
    availabilityChangesOpen: false,
    recommendationsOpen: false,
    selectedPlayerId: '',
    playerViewMode: 'all',
  }), [defaultCaptainTeamIds]);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState<PlanningBoardViewState>({
    userId: currentUserId,
    routeKey: 'planning-board',
    defaultState: defaultViewState,
  });
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>(savedViewState.teamIds);
  const [teamScopeMode, setTeamScopeMode] = useState<'default' | 'explicit'>(savedViewState.teamScopeMode || 'explicit');
  const [selectedPlayerId, setSelectedPlayerId] = useState(savedViewState.selectedPlayerId || '');
  const [playerViewMode, setPlayerViewMode] = useState<PlayerViewMode>(savedViewState.playerViewMode || (savedViewState.selectedPlayerId ? 'player' : 'all'));
  const [dateFrom, setDateFrom] = useState(savedViewState.dateFrom || '');
  const [dateTo, setDateTo] = useState(savedViewState.dateTo || '');
  const [matchPopover, setMatchPopover] = useState<{ anchor: HTMLElement; match: MatchRow; details: string } | null>(null);
  const [coveragePopover, setCoveragePopover] = useState<{ anchor: HTMLElement; date: string; matches: MatchRow[] } | null>(null);
  const [participationPopover, setParticipationPopover] = useState<{ anchor: HTMLElement; row: BoardPlayerRow } | null>(null);
  const playerCentricView = playerViewMode !== 'all';
  const playerColumnWidth = playerCentricView ? 270 : 190;
  const matchColumnWidth = playerCentricView ? 144 : 142;
  const [includeCompleted, setIncludeCompleted] = useState(savedViewState.includeCompleted);
  const [includeSubstitutes, setIncludeSubstitutes] = useState(savedViewState.includeSubstitutes ?? false);
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [rosters, setRosters] = useState<RosterRow[]>([]);
  const [loadedRosterScopeKey, setLoadedRosterScopeKey] = useState('');
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [lineupEntries, setLineupEntries] = useState<LineupEntryRow[]>([]);
  const [standbyPlayers, setStandbyPlayers] = useState<StandbyRow[]>([]);
  const [availability, setAvailability] = useState<AvailabilityRow[]>([]);
  const [lineStatuses, setLineStatuses] = useState<LineStatusRow[]>([]);
  const [participationEvents, setParticipationEvents] = useState<ParticipationEventRow[]>([]);
  const [partnerPreferences, setPartnerPreferences] = useState<PartnerPreferenceRow[]>([]);
  const [participationRows, setParticipationRows] = useState<Array<ParticipationReportRow & { teamId: string }>>([]);
  const [nextParticipationBoundaryAt, setNextParticipationBoundaryAt] = useState<string | null>(null);
  const [planningMatches, setPlanningMatches] = useState<MatchRow[]>([]);
  const [planningRosters, setPlanningRosters] = useState<RosterRow[]>([]);
  const [planningAssignments, setPlanningAssignments] = useState<LineupEntryRow[]>([]);
  const [planningAvailability, setPlanningAvailability] = useState<AvailabilityRow[]>([]);
  const [showParticipationInfo, setShowParticipationInfo] = useState(savedViewState.showParticipationInfo);
  const [sortField, setSortField] = useState<SortField>(normalizeSortField(savedViewState.sortField));
  const [sortDirection, setSortDirection] = useState<SortDirection>(savedViewState.sortDirection);
  const [headerOpen, setHeaderOpen] = useState(savedViewState.headerOpen ?? true);
  const [availabilityChangesOpen, setAvailabilityChangesOpen] = useState(savedViewState.availabilityChangesOpen ?? false);
  const [recommendationsOpen, setRecommendationsOpen] = useState(savedViewState.recommendationsOpen ?? false);
  const [dismissedRecommendationKeys, setDismissedRecommendationKeys] = useState<Set<string>>(new Set());
  const [lineupDialogMatchId, setLineupDialogMatchId] = useState<string | null>(null);
  const [planningDetailPlayerId, setPlanningDetailPlayerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availabilityChanges, setAvailabilityChanges] = useState<AvailabilityChangeAttention[]>([]);
  const [availabilityChangesLoading, setAvailabilityChangesLoading] = useState(false);
  const [availabilityChangesError, setAvailabilityChangesError] = useState<string | null>(null);
  const [groupMeRefreshWarning, setGroupMeRefreshWarning] = useState<string | null>(null);
  const [boardLoaded, setBoardLoaded] = useState(false);
  const restoredViewStateKey = useRef('');
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const autoGroupMeRefreshStartedMatchIds = useRef<Set<string>>(new Set());
  const [viewStateReady, setViewStateReady] = useState(false);

  const teamById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);
  const playerById = useMemo(() => new Map(players.map((player) => [player.id, player])), [players]);
  const positionById = useMemo(() => new Map(positions.map((position) => [position.id, position])), [positions]);
  const matchById = useMemo(() => new Map(matches.map((match) => [match.id, match])), [matches]);
  const selectedTeamIds = teamIds;
  const selectedTeamScopeKey = [...selectedTeamIds].sort().join(',');
  const today = viewerToday();
  const rosterMemberships = useMemo(() => rosters.map((roster) => ({
    teamId: roster.team_id,
    playerId: roster.player_id,
    isActive: roster.is_active,
    rosterStatus: roster.roster_status,
  })), [rosters]);
  const sharedPlayerIds = useMemo(
    () => playersRosteredOnAllSelectedTeams(selectedTeamIds, rosterMemberships).filter((playerId) => playerById.has(playerId)),
    [playerById, rosterMemberships, selectedTeamIds],
  );
  const selectablePlayerIds = useMemo(
    () => playersRosteredOnAnySelectedTeam(selectedTeamIds, rosterMemberships).filter((playerId) => playerById.has(playerId)),
    [playerById, rosterMemberships, selectedTeamIds],
  );
  const selectablePlayers = useMemo(
    () => selectablePlayerIds
      .map((playerId) => playerById.get(playerId))
      .filter((player): player is PlayerRow => Boolean(player))
      .sort((left, right) => comparePlayerNames(left, right, userPreferences)),
    [selectablePlayerIds, playerById, userPreferences],
  );
  const rosterPlayerIdsByTeam = useMemo(() => {
    const byTeam = new Map<string, Set<string>>();
    const memberships = playerCentricView ? planningRosters : rosters;
    memberships.forEach((membership) => {
      const current = byTeam.get(membership.team_id) || new Set<string>();
      current.add(membership.player_id);
      byTeam.set(membership.team_id, current);
    });
    return byTeam;
  }, [playerCentricView, planningRosters, rosters]);
  const selectedPlanningOptions = useMemo<PlanningOptionValue[]>(() => {
    const values: PlanningOptionValue[] = [];
    if (includeCompleted) values.push('showCompletedMatches');
    if (showParticipationInfo) values.push('showParticipationInfo');
    if (includeSubstitutes) values.push('showSubstitutePlayers');
    if (userPreferences.groupPlayersBySex) values.push('groupByGender');
    return values;
  }, [includeCompleted, includeSubstitutes, showParticipationInfo, userPreferences.groupPlayersBySex]);

  const handlePlanningOptionsChange = (event: SelectChangeEvent<PlanningOptionValue[]>) => {
    const rawValue = event.target.value;
    const nextValues = typeof rawValue === 'string'
      ? rawValue.split(',') as PlanningOptionValue[]
      : rawValue;
    const selected = new Set(nextValues);

    setIncludeCompleted(selected.has('showCompletedMatches'));
    setShowParticipationInfo(selected.has('showParticipationInfo'));
    setIncludeSubstitutes(selected.has('showSubstitutePlayers'));
    onUserPreferencesChange({ groupPlayersBySex: selected.has('groupByGender') });
  };
  const summaryColumns = useMemo(
    () => [
      { key: 'played' as const, label: 'Played' },
      { key: 'future' as const, label: 'Future' },
      { key: 'lateCancels' as const, label: 'Late Cancels' },
      { key: 'lateSubs' as const, label: 'Late Subs' },
      { key: 'status' as const, label: 'Status' },
    ].filter(() => showParticipationInfo && !playerCentricView),
    [showParticipationInfo, playerCentricView],
  );
  const fixedColumnCount = 2 + summaryColumns.length;
  const playerColumnLeft = accentColumnWidth;
  const summaryColumnLeft = (index: number) => accentColumnWidth + playerColumnWidth + index * summaryColumnWidth;

  const loadDismissedRecommendations = useCallback(async () => {
    if (!currentUserId) {
      setDismissedRecommendationKeys(new Set());
      return;
    }

    const { data, error: dismissalError } = await supabase
      .from('notification_dismissals')
      .select('notification_key, dismissed_by')
      .eq('dismissed_by', currentUserId)
      .like('notification_key', 'planning:%');

    if (dismissalError) {
      setError(dismissalError.message || 'Unable to load dismissed planning recommendations.');
      return;
    }

    setDismissedRecommendationKeys(dismissedPlanningRecommendationKeysForUser(
      (data || []) as Array<{ notification_key: string; dismissed_by: string | null }>,
      currentUserId,
    ));
  }, [currentUserId]);

  useEffect(() => {
    void loadDismissedRecommendations();
  }, [loadDismissedRecommendations]);

  const loadFilters = useCallback(async () => {
    const teamResult = await supabase.from('teams').select('id, team_name, short_name, season_label, workspace_id, team_color, player_rating_basis, enforce_max_sit, max_weeks_without_match').is('deleted_at', null).eq('is_active', true).order('team_name');

    if (teamResult.error) {
      setError(teamResult.error.message || 'Unable to load planning board filters.');
      return;
    }

    const loadedTeams = (teamResult.data || []) as unknown as TeamOption[];
    setTeams(loadedTeams);

    setTeamIds((current) => {
      const valid = restoreAccessibleTeamSelection(current, defaultCaptainTeamIds, loadedTeams.map((team) => team.id));
      return sameTeamSelection(current, valid) ? current : valid;
    });
  }, [defaultCaptainTeamIds]);

  useEffect(() => {
    void loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (teams.length === 0) return;
    setTeamIds((current) => {
      const valid = restoreAccessibleTeamSelection(current, defaultCaptainTeamIds, teams.map((team) => team.id));
      return sameTeamSelection(current, valid) ? current : valid;
    });
  }, [defaultCaptainTeamIds, teams]);

  useEffect(() => {
    if (teamScopeMode !== 'default') return;
    const scope = teams.length > 0 ? accessibleTeamSelection(defaultCaptainTeamIds, teams.map((team) => team.id)) : defaultCaptainTeamIds;
    setTeamIds((current) => sameTeamSelection(current, scope) ? current : scope);
  }, [defaultCaptainTeamIds, teamScopeMode, teams]);

  const loadBoard = useCallback(async () => {
    setBoardLoaded(false);
    if (selectedTeamIds.length === 0) {
      setMatches([]);
      setRosters([]);
      setLoadedRosterScopeKey('');
      setPlayers([]);
      setPositions([]);
      setLineupEntries([]);
      setStandbyPlayers([]);
      setAvailability([]);
      setLineStatuses([]);
      setParticipationEvents([]);
      setPartnerPreferences([]);
      setParticipationRows([]);
      setNextParticipationBoundaryAt(null);
      setPlanningMatches([]);
      setPlanningRosters([]);
      setPlanningAssignments([]);
      setPlanningAvailability([]);
      setBoardLoaded(true);
      return;
    }

    setLoading(true);
    setError(null);

    let matchQuery = supabase
      .from('match_events')
      .select('id, team_id, league_configuration_id, match_date, timezone, match_time, warmup_time, match_site_type, opponent_name, opponent_average_edr, opponent_rating_basis, opponent_subjective_difficulty, match_status, lineup_status, groupme_event_id')
      .in('team_id', selectedTeamIds)
      .is('deleted_at', null);

    if (!includeCompleted) matchQuery = matchQuery.neq('match_status', 'Completed');
    matchQuery = matchQuery.neq('match_status', 'Cancelled');
    if (!includeCompleted) matchQuery = matchQuery.gte('match_date', new Date(new Date(`${today}T12:00:00Z`).getTime() - 86400000).toISOString().slice(0, 10));

    let rosterQuery = supabase
      .from('team_rosters')
      .select('team_id, player_id, target_match_count, player_tier, roster_status, roster_role, is_active, team_priority')
      .in('team_id', selectedTeamIds)
      .is('deleted_at', null)
      .eq('is_active', true)
      .neq('roster_status', 'inactive');
    if (!includeSubstitutes) {
      rosterQuery = rosterQuery.neq('roster_role', 'designated_substitute').neq('roster_status', 'substitute');
    }

    const [matchResult, rosterResult] = await Promise.all([matchQuery, rosterQuery]);
    if (matchResult.error || rosterResult.error) {
      setLoading(false);
      setBoardLoaded(true);
      setError(matchResult.error?.message || rosterResult.error?.message || 'Unable to load planning board.');
      return;
    }

    let loadedMatches = ((matchResult.data || []) as unknown as MatchRow[]).filter((match) => includeCompleted || localEventDate(match) >= today).sort(
      (a, b) =>
        localEventDate(a).localeCompare(localEventDate(b)) ||
        (a.match_time || '').localeCompare(arrivalTime(b) || '') ||
        (teamById.get(a.team_id)?.team_name || '').localeCompare(teamById.get(b.team_id)?.team_name || '') ||
        (a.opponent_name || '').localeCompare(b.opponent_name || ''),
    );
    const loadedRosters = (rosterResult.data || []) as unknown as RosterRow[];
    const playerIds = [...new Set(loadedRosters.map((roster) => roster.player_id))];
    // The visible board can be narrowed to one or more teams, but planning
    // pressure must account for the selected players' real assignments across
    // every team the current user can access. RLS remains the authority for
    // which rows are returned here.
    const planningTeamIds = teams.map((team) => team.id);
    const [planningRosterResult, planningMatchResult] = await Promise.all([
      playerIds.length > 0 && planningTeamIds.length > 0
        ? supabase
            .from('team_rosters')
            .select('team_id, player_id, target_match_count, player_tier, roster_status, roster_role, is_active, team_priority')
            .in('team_id', planningTeamIds)
            .in('player_id', playerIds)
            .is('deleted_at', null)
            .eq('is_active', true)
            .neq('roster_status', 'inactive')
        : Promise.resolve({ data: [], error: null }),
      planningTeamIds.length > 0
        ? supabase
            .from('match_events')
            .select('id, team_id, league_configuration_id, match_date, timezone, match_time, warmup_time, match_site_type, opponent_name, opponent_average_edr, opponent_rating_basis, opponent_subjective_difficulty, match_status, lineup_status, groupme_event_id')
            .in('team_id', planningTeamIds)
            .is('deleted_at', null)
            .neq('match_status', 'Cancelled')
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (planningRosterResult.error || planningMatchResult.error) {
      setLoading(false);
      setBoardLoaded(true);
      setError(planningRosterResult.error?.message || planningMatchResult.error?.message || 'Unable to load cross-team planning context.');
      return;
    }
    const loadedPlanningRosters = (planningRosterResult.data || []) as unknown as RosterRow[];
    const loadedPlanningMatches = (planningMatchResult.data || []) as unknown as MatchRow[];
    if (playerCentricView) {
      loadedMatches = playerViewMatchesForPlayers(loadedPlanningMatches, loadedPlanningRosters, playerIds, includeCompleted, today);
    }
    // Load complete lineup/availability context for the expanded schedule, not just focus-team matches.
    const matchIds = loadedMatches.map((match) => match.id);
    const scheduleTeamIds = [...new Set([...selectedTeamIds, ...loadedMatches.map((match) => match.team_id)])];
    const selectedWorkspaceIds = [...new Set(scheduleTeamIds.map((teamId) => teamById.get(teamId)?.workspace_id).filter(Boolean))] as string[];
    const planningMatchIds = loadedPlanningMatches.map((match) => match.id);
    const [planningEntryResult, planningAvailabilityResult, historicalPlayedResult] = await Promise.all([
      planningMatchIds.length > 0 && playerIds.length > 0
        ? supabase.from('lineup_entries').select('match_event_id, league_configuration_position_id, slot_number, player_id').in('match_event_id', planningMatchIds).in('player_id', playerIds).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      planningMatchIds.length > 0 && playerIds.length > 0
        ? supabase.from('availability_records').select('match_event_id, player_id, availability_status').in('match_event_id', planningMatchIds).in('player_id', playerIds).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      loadHistoricalPlayedEvents([...planningMatchIds, ...matchIds]),
    ]);
    if (planningEntryResult.error || planningAvailabilityResult.error || historicalPlayedResult.error) {
      setLoading(false);
      setBoardLoaded(true);
      setError(planningEntryResult.error?.message || planningAvailabilityResult.error?.message || historicalPlayedResult.error?.message || 'Unable to load cross-team planning assignments.');
      return;
    }

    const [playerResult, positionResult, entryResult, standbyResult, availabilityResult, lineStatusResult, eventResult, partnerResult, participationResult] = await Promise.all([
      playerIds.length > 0
        ? supabase.from('players').select('id, first_name, last_name, sex, estimated_dynamic_rating, back_to_back_match_day_tolerance, same_day_match_tolerance, minimum_recovery_hours').in('id', playerIds).is('deleted_at', null).eq('is_active', true)
        : Promise.resolve({ data: [], error: null }),
      supabase.from('league_configuration_positions').select('id, league_configuration_id, position_code, line_type, display_order, required_player_count, required_male_count, required_female_count').is('deleted_at', null).eq('is_active', true),
      matchIds.length > 0
        ? supabase.from('lineup_entries').select('match_event_id, league_configuration_position_id, slot_number, player_id').in('match_event_id', matchIds).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      matchIds.length > 0
        ? supabase.from('standby_players').select('match_event_id, player_id').in('match_event_id', matchIds).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      matchIds.length > 0 && playerIds.length > 0
        ? supabase.from('availability_records').select('match_event_id, player_id, availability_status').in('match_event_id', matchIds).in('player_id', playerIds).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      matchIds.length > 0
        ? supabase.from('lineup_position_statuses').select('match_event_id, league_configuration_position_id, line_status').in('match_event_id', matchIds)
        : Promise.resolve({ data: [], error: null }),
      matchIds.length > 0 && playerIds.length > 0
        ? supabase
            .from('player_participation_events')
            .select('match_event_id, player_id, league_configuration_position_id, participation_event_type, allocation_count_indicator, participation_event_reason')
            .in('match_event_id', matchIds)
            .in('player_id', playerIds)
            .in('participation_event_type', ['played', 'cancelled_late', 'replacement', 'late_substitute', 'standby_promotion'])
            .is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      selectedWorkspaceIds.length > 0
        ? supabase
            .from('partner_preferences')
            .select('id, workspace_id, team_id, player1_id, player2_id, compatibility, pairing_rule, is_active, notes')
            .in('workspace_id', selectedWorkspaceIds)
            .eq('is_active', true)
            .is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      Promise.all(
        selectedTeamIds.map(async (teamId) => ({
          teamId,
          report: await loadParticipationReportRows({
            teamId,
            includeInactivePlayers: false,
            includeSubstitutePlayers: includeSubstitutes,
            userPreferences,
          }),
        })),
      )
        .then((data) => ({ data, error: null as Error | null }))
        .catch((error: unknown) => ({ data: [] as Array<{ teamId: string; report: { rows: ParticipationReportRow[]; nextBoundaryAt: string | null } }>, error: error instanceof Error ? error : new Error('Unable to load participation summaries.') })),
    ]);

    const loadError =
      playerResult.error ||
      positionResult.error ||
      entryResult.error ||
      standbyResult.error ||
      availabilityResult.error ||
      lineStatusResult.error ||
      eventResult.error ||
      participationResult.error;
    if (loadError) {
      setLoading(false);
      setBoardLoaded(true);
      setError(loadError.message);
      return;
    }

    setMatches(loadedMatches);
    setRosters(loadedRosters);
    setLoadedRosterScopeKey([...selectedTeamIds].sort().join(','));
    setPlayers((playerResult.data || []) as unknown as PlayerRow[]);
    setPositions((positionResult.data || []) as unknown as PositionRow[]);
    setLineupEntries(effectiveHistoricalLineup((entryResult.data || []) as unknown as LineupEntryRow[], historicalPlayedResult.data || []));
    setStandbyPlayers((standbyResult.data || []) as unknown as StandbyRow[]);
    setAvailability((availabilityResult.data || []) as unknown as AvailabilityRow[]);
    setLineStatuses((lineStatusResult.data || []) as unknown as LineStatusRow[]);
    setParticipationEvents((eventResult.data || []) as unknown as ParticipationEventRow[]);
    setPartnerPreferences(partnerResult.error ? [] : ((partnerResult.data || []) as unknown as PartnerPreferenceRow[]));
    setParticipationRows(participationResult.data.flatMap((result) => result.report.rows.map((row) => ({ ...row, teamId: result.teamId }))));
    setNextParticipationBoundaryAt(participationResult.data.map((result) => result.report.nextBoundaryAt).filter((value): value is string => Boolean(value)).sort()[0] || null);
    setPlanningMatches(loadedPlanningMatches);
    setPlanningRosters(loadedPlanningRosters);
    setPlanningAssignments(effectiveHistoricalLineup((planningEntryResult.data || []) as unknown as LineupEntryRow[], historicalPlayedResult.data || []));
    setPlanningAvailability((planningAvailabilityResult.data || []) as unknown as AvailabilityRow[]);
    setLoading(false);
    setBoardLoaded(true);
  }, [includeCompleted, includeSubstitutes, playerCentricView, selectedTeamIds, teamById, teams, today, userPreferences]);

  useEffect(() => {
    void loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') void loadBoard(); };
    document.addEventListener('visibilitychange', onVisible);
    const delay = nextParticipationBoundaryAt
      ? Math.min(24 * 60 * 60 * 1000, Math.max(60 * 1000, Date.parse(nextParticipationBoundaryAt) - Date.now() + 1000))
      : null;
    const timer = delay === null ? null : window.setTimeout(() => {
      if (document.visibilityState === 'visible') void loadBoard();
    }, delay);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [loadBoard, nextParticipationBoundaryAt]);

  useEffect(() => {
    if (playerViewMode !== 'player' || !selectedPlayerId) return;
    if (selectedTeamIds.length === 0 || (loadedRosterScopeKey === selectedTeamScopeKey && !selectablePlayerIds.includes(selectedPlayerId))) {
      setSelectedPlayerId('');
      setPlayerViewMode('all');
    }
  }, [loadedRosterScopeKey, playerViewMode, selectablePlayerIds, selectedPlayerId, selectedTeamIds.length, selectedTeamScopeKey]);

  const replayGroupMeMatchRsvpFallback = useCallback(async (match: MatchRow) => {
    const { error: replayError } = await supabase.rpc('replay_groupme_match_event_rsvps', {
      p_match_event_id: match.id,
    });
    if (replayError) throw replayError;
  }, []);

  const refreshGroupMeMatchBatch = useCallback(async (matchesToRefresh: MatchRow[]) => {
    const response = await fetch('/api/groupme/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'refresh',
        trigger: 'planning_board_load',
        items: matchesToRefresh.map((match) => ({ kind: 'match', id: match.id })),
      }),
    });
    const payload = await response.json().catch(() => ({} as GroupMeRefreshResponse)) as GroupMeRefreshResponse;
    if (!response.ok) {
      throw new Error(payload.error || `GroupMe refresh failed with ${response.status}.`);
    }
    return payload.results || [];
  }, []);

  useEffect(() => {
    if (!currentUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setTeamIds(savedViewState.teamScopeMode === 'default' ? defaultCaptainTeamIds : savedViewState.teamIds);
    setTeamScopeMode(savedViewState.teamScopeMode || 'explicit');
    setSelectedPlayerId(savedViewState.selectedPlayerId || '');
    setDateFrom(savedViewState.dateFrom || '');
    setDateTo(savedViewState.dateTo || '');
    setPlayerViewMode(savedViewState.playerViewMode || (savedViewState.selectedPlayerId ? 'player' : 'all'));
    setIncludeCompleted(savedViewState.includeCompleted);
    setIncludeSubstitutes(savedViewState.includeSubstitutes ?? false);
    setShowParticipationInfo(savedViewState.showParticipationInfo);
    setSortField(normalizeSortField(savedViewState.sortField));
    setSortDirection(savedViewState.sortDirection);
    setHeaderOpen(savedViewState.headerOpen ?? true);
    setAvailabilityChangesOpen(savedViewState.availabilityChangesOpen ?? false);
    setRecommendationsOpen(savedViewState.recommendationsOpen ?? false);
    onFullScreenChange?.(savedViewState.fullScreen ?? false);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [currentUserId, defaultCaptainTeamIds, loadedStateKey, onFullScreenChange, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({
      teamIds,
      teamScopeMode,
      includeCompleted,
      includeSubstitutes,
      showParticipationInfo,
      sortField,
      sortDirection,
      fullScreen: Boolean(isFullScreen),
      headerOpen,
      availabilityChangesOpen,
      recommendationsOpen,
      selectedPlayerId,
      playerViewMode,
      dateFrom,
      dateTo,
    });
  }, [dateFrom, dateTo, availabilityChangesOpen, headerOpen, includeCompleted, includeSubstitutes, isFullScreen, playerViewMode, recommendationsOpen, selectedPlayerId, setSavedViewState, showParticipationInfo, sortDirection, sortField, teamIds, teamScopeMode, viewStateKey, viewStateReady]);

  usePersistentScrollRestoration({
    userId: currentUserId,
    routeKey: 'planning-board',
    contextKey: selectedTeamIds.join(',') || 'default',
    ready: viewStateReady && boardLoaded && !loading,
    elementRef: gridScrollRef,
  });

  const availabilityByCell = useMemo(() => new Map(availability.map((row) => [`${row.match_event_id}:${row.player_id}`, row.availability_status])), [availability]);
  const entriesByMatchPlayer = useMemo(() => {
    const map = new Map<string, LineupEntryRow[]>();
    lineupEntries.forEach((entry) => {
      const key = `${entry.match_event_id}:${entry.player_id}`;
      map.set(key, [...(map.get(key) || []), entry]);
    });
    return map;
  }, [lineupEntries]);
  const entriesByMatchPosition = useMemo(() => {
    const map = new Map<string, LineupEntryRow[]>();
    lineupEntries.forEach((entry) => {
      const key = `${entry.match_event_id}:${entry.league_configuration_position_id}`;
      map.set(key, [...(map.get(key) || []), entry]);
    });
    return map;
  }, [lineupEntries]);
  const entriesByMatch = useMemo(() => {
    const map = new Map<string, LineupEntryRow[]>();
    lineupEntries.forEach((entry) => {
      map.set(entry.match_event_id, [...(map.get(entry.match_event_id) || []), entry]);
    });
    return map;
  }, [lineupEntries]);
  const standbyByMatchPlayer = useMemo(() => new Set(standbyPlayers.map((row) => `${row.match_event_id}:${row.player_id}`)), [standbyPlayers]);
  const lineStatusByPosition = useMemo(
    () => new Map(lineStatuses.map((row) => [`${row.match_event_id}:${row.league_configuration_position_id}`, row.line_status])),
    [lineStatuses],
  );
  const rosterTierByTeamPlayer = useMemo(
    () => new Map(rosters.map((row) => [`${row.team_id}:${row.player_id}`, row.player_tier])),
    [rosters],
  );
  const eventsByMatchPlayer = useMemo(() => {
    const map = new Map<string, ParticipationEventRow[]>();
    participationEvents.forEach((event) => {
      const key = `${event.match_event_id}:${event.player_id}`;
      map.set(key, [...(map.get(key) || []), event]);
    });
    return map;
  }, [participationEvents]);
  const conflictByDatePlayer = useMemo(() => {
    const assignments = new Map<string, Array<{ match: MatchRow; position: PositionRow | undefined }>>();
    lineupEntries.forEach((entry) => {
      const match = matchById.get(entry.match_event_id);
      if (!match || match.match_status === 'Cancelled' || !['Draft', 'Published'].includes(match.lineup_status || 'Not Started')) return;
      const key = `${localEventDate(match)}:${entry.player_id}`;
      assignments.set(key, [...(assignments.get(key) || []), { match, position: positionById.get(entry.league_configuration_position_id) }]);
    });
    return assignments;
  }, [lineupEntries, matchById, positionById]);
  const lineupLoadWarningsByCell = useMemo(
    () => buildThreeDayLineupWarnings(
      planningMatches.map<LineupLoadMatch>((match) => ({
        id: match.id,
        teamId: match.team_id,
        matchDate: match.match_date,
        opponentName: match.opponent_name,
        lineupStatus: match.lineup_status,
        matchStatus: match.match_status,
      })),
      planningAssignments.map<LineupLoadEntry>((entry) => ({ matchId: entry.match_event_id, playerId: entry.player_id })),
    ),
    [planningAssignments, planningMatches],
  );
  const availabilityAttentionByCell = useMemo(() => latestAvailabilityChangeByCell(availabilityChanges), [availabilityChanges]);

  const loadAvailabilityAttention = useCallback(async () => {
    setAvailabilityChangesLoading(true);
    try {
      const changes = await loadUnreviewedAvailabilityChanges();
      setAvailabilityChanges(changes);
      setAvailabilityChangesError(null);
    } catch (loadError) {
      setAvailabilityChangesError(loadError instanceof Error ? loadError.message : 'Unable to load availability changes.');
    } finally {
      setAvailabilityChangesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUserId) {
      setAvailabilityChanges([]);
      return;
    }
    void loadAvailabilityAttention();
  }, [currentUserId, loadAvailabilityAttention]);

  useEffect(() => {
    if (!canRefreshGroupMeEvents || matches.length === 0) return;

    const matchesToRefresh = matches.filter((match) => (
      match.groupme_event_id &&
      !autoGroupMeRefreshStartedMatchIds.current.has(match.id)
    ));
    if (matchesToRefresh.length === 0) return;

    matchesToRefresh.forEach((match) => autoGroupMeRefreshStartedMatchIds.current.add(match.id));
    let cancelled = false;

    const refreshLinkedGroupMeAvailability = async () => {
      let refreshedCount = 0;
      let fallbackCount = 0;
      let failedCount = 0;
      const batchSize = 4;

      for (let index = 0; index < matchesToRefresh.length; index += batchSize) {
        const batch = matchesToRefresh.slice(index, index + batchSize);
        try {
          const results = await refreshGroupMeMatchBatch(batch);
          const failedIds = new Set(
            results
              .filter((result) => result.status === 'failed' || result.status === 'skipped')
              .map((result) => result.id)
              .filter((id): id is string => Boolean(id)),
          );
          refreshedCount += batch.length - failedIds.size;

          for (const match of batch.filter((row) => failedIds.has(row.id))) {
            try {
              await replayGroupMeMatchRsvpFallback(match);
              fallbackCount += 1;
            } catch {
              failedCount += 1;
            }
          }
        } catch {
          for (const match of batch) {
            try {
              await replayGroupMeMatchRsvpFallback(match);
              fallbackCount += 1;
            } catch {
              failedCount += 1;
            }
          }
        }
      }

      if (cancelled || refreshedCount + fallbackCount === 0) {
        if (!cancelled && failedCount > 0) {
          setGroupMeRefreshWarning(`Unable to refresh availability for ${failedCount} linked GroupMe match${failedCount === 1 ? '' : 'es'}.`);
        }
        return;
      }

      await loadBoard();
      await loadAvailabilityAttention();
      if (!cancelled) {
        setGroupMeRefreshWarning(
          failedCount > 0
            ? `Refreshed linked GroupMe availability for ${refreshedCount + fallbackCount} match${refreshedCount + fallbackCount === 1 ? '' : 'es'}, but ${failedCount} could not be refreshed.`
            : null,
        );
      }
    };

    void refreshLinkedGroupMeAvailability();

    return () => {
      cancelled = true;
    };
  }, [canRefreshGroupMeEvents, loadAvailabilityAttention, loadBoard, matches, refreshGroupMeMatchBatch, replayGroupMeMatchRsvpFallback]);

  const clearReviewedChanges = async () => {
    if (!currentUserId || availabilityChanges.length === 0) return;
    setAvailabilityChangesLoading(true);
    try {
      await markAvailabilityChangesReviewed(currentUserId, availabilityChanges);
      setAvailabilityChanges([]);
      setAvailabilityChangesError(null);
      onAvailabilityChangesReviewed?.();
    } catch (clearError) {
      setAvailabilityChangesError(clearError instanceof Error ? clearError.message : 'Unable to clear reviewed changes.');
    } finally {
      setAvailabilityChangesLoading(false);
    }
  };

  const boardRows = useMemo(() => {
    return participationRows
      .filter((row) => selectedTeamIds.includes(row.teamId))
      .map<BoardPlayerRow>((row) => {
        const player = playerById.get(row.playerId);
        return {
          id: `${row.teamId}:${row.playerId}`,
          teamId: row.teamId,
          seasonId: row.teamId,
          playerId: row.playerId,
          playerName: row.playerName,
          firstName: row.firstName,
          lastName: row.lastName,
          playerSex: player?.sex || null,
          totalPlayed: row.totalPlayed,
          countedPlayed: row.countedPlayed,
          provisionalPlayed: row.provisionalPlayed,
          singlesPlayed: row.singlesPlayed,
          doublesPlayed: row.doublesPlayed,
          lateCancels: row.lateCancels,
          lateSubstitutes: row.lateSubstitutes,
          futureSingles: row.futureSingles,
          futureDoubles: row.futureDoubles,
          statusDelta: row.statusDelta,
          participationTarget: row.adjustedTarget,
          lastPlayedWeeksAgo: row.lastPlayedWeeksAgo,
          nextMatchWeeksAway: row.nextMatchWeeksAway,
          homeMatches: row.homeMatches,
          awayMatches: row.awayMatches,
          neutralMatches: row.neutralMatches,
          unknownSiteMatches: row.unknownSiteMatches,
          totalCompletedAndFutureScheduledMatches: row.totalCompletedAndFutureScheduledMatches,
          homeMatchPercentage: row.homeMatchPercentage,
        };
      })
      .sort((a, b) => {
        const teamCompare = (teamById.get(a.teamId)?.team_name || '').localeCompare(teamById.get(b.teamId)?.team_name || '');
        if (teamCompare) return teamCompare;
        const direction = sortDirection === 'asc' ? 1 : -1;
        let result = 0;
        if (sortField === 'status') {
          result = a.statusDelta - b.statusDelta;
        }
        if (result !== 0) return result * direction;
        return comparePlayerNames(
          playerById.get(a.playerId) || { first_name: '', last_name: '' },
          playerById.get(b.playerId) || { first_name: '', last_name: '' },
          userPreferences,
        );
      });
  }, [participationRows, playerById, selectedTeamIds, sortDirection, sortField, teamById, userPreferences]);

  const playerCentricRows = useMemo(() => {
    const playerIds = playerViewMode === 'shared'
      ? sharedPlayerIds
      : playerViewMode === 'player' && selectedPlayerId && selectablePlayerIds.includes(selectedPlayerId)
        ? [selectedPlayerId]
        : selectablePlayerIds;
    return playerIds.map((playerId) => {
      const rows = boardRows.filter((row) => row.playerId === playerId);
      const player = playerById.get(playerId);
      const totalCompletedAndFutureScheduledMatches = rows.reduce((sum, row) => sum + row.totalCompletedAndFutureScheduledMatches, 0);
      const homeMatches = rows.reduce((sum, row) => sum + row.homeMatches, 0);
      const first = rows[0];
      return {
        id: `cross-team:${playerId}`,
        teamId: '__cross-team__',
        seasonId: '',
        playerId,
        playerName: formatPlayerName(player, userPreferences) || first?.playerName || 'Player',
        firstName: player?.first_name || first?.firstName || '',
        lastName: player?.last_name || first?.lastName || '',
        playerSex: player?.sex || first?.playerSex || null,
        totalPlayed: rows.reduce((sum, row) => sum + row.totalPlayed, 0),
        countedPlayed: rows.reduce((sum, row) => sum + row.countedPlayed, 0),
        provisionalPlayed: rows.reduce((sum, row) => sum + row.provisionalPlayed, 0),
        singlesPlayed: rows.reduce((sum, row) => sum + row.singlesPlayed, 0),
        doublesPlayed: rows.reduce((sum, row) => sum + row.doublesPlayed, 0),
        lateCancels: rows.reduce((sum, row) => sum + row.lateCancels, 0),
        lateSubstitutes: rows.reduce((sum, row) => sum + row.lateSubstitutes, 0),
        futureSingles: rows.reduce((sum, row) => sum + row.futureSingles, 0),
        futureDoubles: rows.reduce((sum, row) => sum + row.futureDoubles, 0),
        statusDelta: rows.reduce((sum, row) => sum + row.statusDelta, 0),
        lastPlayedWeeksAgo: rows.reduce<number | null>((nearest, row) => nearest == null ? row.lastPlayedWeeksAgo : row.lastPlayedWeeksAgo == null ? nearest : Math.min(nearest, row.lastPlayedWeeksAgo), null),
        nextMatchWeeksAway: rows.reduce<number | null>((nearest, row) => nearest == null ? row.nextMatchWeeksAway : row.nextMatchWeeksAway == null ? nearest : Math.min(nearest, row.nextMatchWeeksAway), null),
        homeMatches,
        awayMatches: rows.reduce((sum, row) => sum + row.awayMatches, 0),
        neutralMatches: rows.reduce((sum, row) => sum + row.neutralMatches, 0),
        unknownSiteMatches: rows.reduce((sum, row) => sum + row.unknownSiteMatches, 0),
        totalCompletedAndFutureScheduledMatches,
        homeMatchPercentage: totalCompletedAndFutureScheduledMatches > 0 ? Math.round((homeMatches / totalCompletedAndFutureScheduledMatches) * 100) : null,
      } satisfies BoardPlayerRow;
    }).sort((left, right) => comparePlayerNames(
      playerById.get(left.playerId) || { first_name: left.firstName, last_name: left.lastName },
      playerById.get(right.playerId) || { first_name: right.firstName, last_name: right.lastName },
      userPreferences,
    ));
  }, [boardRows, playerById, playerViewMode, selectablePlayerIds, selectedPlayerId, sharedPlayerIds, userPreferences]);

  const displayedBoardRows = useMemo(() => {
    const rows = playerCentricView ? playerCentricRows : boardRows;
    return userPreferences.groupPlayersBySex ? [...rows].sort((left, right) => (left.playerSex === 'male' ? 0 : left.playerSex === 'female' ? 1 : 2) - (right.playerSex === 'male' ? 0 : right.playerSex === 'female' ? 1 : 2)) : rows;
  }, [playerCentricView, playerCentricRows, boardRows, userPreferences.groupPlayersBySex]);
  const displayedTeamIds = playerCentricView ? ['__cross-team__'] : selectedTeamIds;

  const rowsByTeam = useMemo(() => {
    const map = new Map<string, BoardPlayerRow[]>();
    displayedBoardRows.forEach((row) => map.set(row.teamId, [...(map.get(row.teamId) || []), row]));
    return map;
  }, [displayedBoardRows]);

  const visibleMatches = useMemo(() => playerCentricView
    ? playerViewMatchesForPlayers(matches, planningRosters, displayedBoardRows.map((row) => row.playerId), includeCompleted, today)
    : matches, [playerCentricView, matches, planningRosters, displayedBoardRows, includeCompleted, today]);
  const groupedDates = useMemo(() => groupPlayerViewDates(visibleMatches, dateFrom, dateTo), [visibleMatches, dateFrom, dateTo]);
  const matchesByDate = useMemo(() => new Map(groupedDates.map((day) => [day.date, day.matches])), [groupedDates]);
  const matchColumns = useMemo<MatchColumn[]>(() => groupedDates.map((day) => ({ key: day.date, match_date: day.date })), [groupedDates]);
  const tableMinWidth = accentColumnWidth + playerColumnWidth + summaryColumns.length * summaryColumnWidth + matchColumns.length * matchColumnWidth;

  const matchesForTeamColumn = (teamId: string, column: MatchColumn): MatchRow[] => {
    return (matchesByDate.get(column.match_date) || []).filter((match) => teamId === '__cross-team__' || match.team_id === teamId);
  };

  const openLineupDialog = (matchId: string) => {
    if (onOpenLineup) {
      onOpenLineup(matchId, { teamId: matchById.get(matchId)?.team_id || null });
      return;
    }
    setLineupDialogMatchId(matchId);
  };

  const closeLineupDialog = async () => {
    setLineupDialogMatchId(null);
    await loadBoard();
  };

  const openAvailabilityFromMatch = (match: MatchRow) => {
    onOpenAvailability?.({ teamId: match.team_id, matchId: match.id, returnTarget: 'planning-board' });
  };

  const openMatchDetails = (match: MatchRow) => {
    if (onOpenMatchDetail) {
      onOpenMatchDetail(match.id);
      return;
    }
    openLineupDialog(match.id);
  };

  const edrIndicatorForMatch = (match: MatchRow): PlanningBoardEdrIndicator => {
    const playersForMatch = new Map(
      players.map((player) => [
        player.id,
        {
          ...player,
          player_tier: rosterTierByTeamPlayer.get(`${match.team_id}:${player.id}`) || null,
        },
      ]),
    );
    return buildPlanningBoardCompetitivenessIndicator({
      match,
      team: teamById.get(match.team_id),
      positions,
      entries: entriesByMatch.get(match.id) || [],
      playersById: playersForMatch,
      lineStatusesByPositionId: new Map(
        positions
          .filter((position) => position.league_configuration_id === match.league_configuration_id)
          .map((position) => [position.id, lineStatusByPosition.get(`${match.id}:${position.id}`) || 'normal']),
      ),
    });
  };

  const openSlotsIndicatorForMatch = (match: MatchRow): PlanningBoardOpenSlotsIndicator => buildPlanningBoardOpenSlotsIndicator({
    match,
    positions,
    entries: entriesByMatch.get(match.id) || [],
    lineStatusesByPositionId: new Map(
      positions
        .filter((position) => position.league_configuration_id === match.league_configuration_id)
        .map((position) => [position.id, lineStatusByPosition.get(`${match.id}:${position.id}`) || 'normal']),
    ),
  });

  const coverageByMatch = useMemo(() => new Map(matches.map((match) => [match.id, playerViewCoverage(match, positions, entriesByMatch.get(match.id) || [], new Map(positions.map((position) => [position.id, lineStatusByPosition.get(`${match.id}:${position.id}`)])))])), [matches, positions, entriesByMatch, lineStatusByPosition]);
  const coverageForMatch = (match: MatchRow) => coverageByMatch.get(match.id)!;

  const edrIndicatorSx = (indicator: PlanningBoardEdrIndicator) => {
    const color = indicator.tone === 'positive'
      ? '#1b5e20'
      : indicator.tone === 'negative'
        ? '#8a3a00'
        : indicator.tone === 'unavailable'
          ? 'text.secondary'
          : 'text.primary';
    return {
      height: 18,
      maxWidth: 86,
      bgcolor: 'transparent',
      color,
      borderColor: 'divider',
      '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem', fontWeight: 800 },
    } as const;
  };

  const renderEdrIndicator = (match: MatchRow) => {
    const indicator = edrIndicatorForMatch(match);
    if (!indicator.show) return null;
    return (
      <Tooltip title={indicator.tooltip || ''} arrow>
        <Chip
          size="small"
          variant="outlined"
          label={indicator.label}
          sx={edrIndicatorSx(indicator)}
          data-testid="planning-board-edr-differential"
        />
      </Tooltip>
    );
  };

  const renderOpenSlotsIndicator = (match: MatchRow) => {
    const indicator = openSlotsIndicatorForMatch(match);
    if (!indicator.show) return null;
    return (
      <Tooltip title={indicator.tooltip} arrow>
        <Typography
          variant="caption"
          component="div"
          data-testid="planning-board-open-lineup-slots"
          sx={{
            color: 'error.main',
            fontWeight: 900,
            lineHeight: 1.05,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {indicator.label}
        </Typography>
      </Tooltip>
    );
  };

  const cellFor = (row: BoardPlayerRow, match: MatchRow): BoardCell => {
    const matchBelongsToTeam = row.teamId === '__cross-team__' || match.team_id === row.teamId;
    if (!matchBelongsToTeam) return { text: '', tooltip: '', matchBelongsToTeam: false, attention: null };
    if (!rosterPlayerIdsByTeam.get(match.team_id)?.has(row.playerId)) {
      return {
        text: '—',
        tooltip: `${row.playerName}\n${teamById.get(match.team_id)?.team_name || 'Team'} vs ${match.opponent_name || 'TBD'}\nNot rostered on this team.`,
        matchBelongsToTeam: true,
        attention: null,
      };
    }

    const player = playerById.get(row.playerId);
    const entries = entriesByMatchPlayer.get(`${match.id}:${row.playerId}`) || [];
    const events = eventsByMatchPlayer.get(`${match.id}:${row.playerId}`) || [];
    const availabilityStatus = availabilityByCell.get(`${match.id}:${row.playerId}`) || 'unknown';
    const attention = availabilityAttentionByCell.get(`${match.id}:${row.playerId}`) || null;
    const lineupLoadWarning = lineupLoadWarningsByCell.get(`${match.id}:${row.playerId}`) || null;
    const eventText = events
      .map((event) => {
        if (event.participation_event_type === 'cancelled_late') return 'LC';
        if (['replacement', 'late_substitute'].includes(event.participation_event_type)) return 'LS';
        if (event.participation_event_type === 'standby_promotion') return 'LS';
        return '';
      })
      .filter(Boolean);
    const lineTexts = entries.map((entry) => positionById.get(entry.league_configuration_position_id)?.position_code || 'Line');
    const defaultTexts = [...new Set(entries.map((entry) => defaultedLineLabel(lineStatusByPosition.get(`${match.id}:${entry.league_configuration_position_id}`))).filter(Boolean))];
    const standbyText = standbyByMatchPlayer.has(`${match.id}:${row.playerId}`) ? ['SB'] : [];
    const text = [...new Set([...defaultTexts, ...lineTexts, ...standbyText, ...eventText])].join(' / ');
    const maybeAssignment = availabilityStatus === 'maybe' && entries.length > 0;
    const conflictAssignments = conflictByDatePlayer.get(`${localEventDate(match)}:${row.playerId}`) || [];
    const otherAssignments = conflictAssignments.filter((assignment) => assignment.match.id !== match.id && scheduledTimesOverlap(match, assignment.match));
    const conflict = otherAssignments.length > 0
      ? otherAssignments.map((assignment) => `${teamById.get(assignment.match.team_id)?.team_name || 'Team'} vs ${assignment.match.opponent_name || 'TBD'}, ${assignment.position?.position_code || 'Line'}`).join('; ')
      : '';
    const partnerNames = entries
      .flatMap((entry) => entriesByMatchPosition.get(`${match.id}:${entry.league_configuration_position_id}`) || [])
      .filter((entry) => entry.player_id !== row.playerId)
      .map((entry) => formatPlayerName(playerById.get(entry.player_id), userPreferences))
      .filter(Boolean);
    const partnerDetails = entries
      .flatMap((entry) => entriesByMatchPosition.get(`${match.id}:${entry.league_configuration_position_id}`) || [])
      .filter((entry) => entry.player_id !== row.playerId)
      .map((entry) => {
        const workspaceId = teamById.get(match.team_id)?.workspace_id || '';
        const preference = workspaceId ? partnerPreferenceForPair(partnerPreferences, workspaceId, row.playerId, entry.player_id) : null;
        if (!preference) return null;
        return {
          partnerName: formatPlayerName(playerById.get(entry.player_id), userPreferences),
          compatibility: normalizeCompatibility(preference?.compatibility),
          pairingRule: normalizePairingRule(preference?.pairing_rule),
          notes: preference?.notes || '',
        };
      }).filter((detail) => detail !== null);
    const tooltip = [
      row.playerName,
      `${teamById.get(match.team_id)?.team_name || 'Team'} vs ${match.opponent_name || 'TBD'}`,
      `${formatDate(localEventDate(match))}${formatTime(match.match_time, timeFormat) ? ` ${formatTime(match.match_time, timeFormat)}` : ''}`,
      `Availability: ${availabilityStyles[availabilityStatus].label}`,
      text ? `Assignment: ${text}` : 'Assignment: None',
      partnerNames.length > 0 ? `Partner: ${partnerNames.join(' / ')}` : '',
      ...partnerDetails.map((detail) => `Partner Compatibility${detail.partnerName ? ` with ${detail.partnerName}` : ''}: ${detail.compatibility}${detail.notes ? ` (${detail.notes})` : ''}`),
      ...partnerDetails.filter((detail) => detail.pairingRule !== 'none').map((detail) => `Scheduling Preference${detail.partnerName ? ` with ${detail.partnerName}` : ''}: ${pairingRuleLabels[detail.pairingRule]}`),
      `Lineup Status: ${match.lineup_status || 'Not Started'}`,
      events.length > 0 ? `Participation Event: ${events.map((event) => event.participation_event_type.replace(/_/g, ' ')).join(', ')}` : '',
      attention ? `Changed: ${availabilityStatusReviewLabel(attention.previous_status)} to ${availabilityStatusReviewLabel(attention.new_status)}` : '',
      maybeAssignment ? 'Assigned while marked Maybe. Confirm availability before the match.' : '',
      conflict ? `Also assigned: ${conflict}` : '',
      lineupLoadWarning ? `Lineup load warning: ${lineupLoadWarning.assignments.length} lineup assignments within a three-day span (${lineupLoadWarning.assignments.map((assignment) => formatDate(assignment.matchDate)).filter((date, index, dates) => dates.indexOf(date) === index).join(', ')})` : '',
      player ? '' : 'Player record missing',
    ].filter(Boolean).join('\n');

    return { availability: availabilityStatus, text, tooltip, conflict, matchBelongsToTeam: true, attention, lineupLoadWarning, maybeAssignment };
  };

  const lineupLoadRecommendations = useMemo(() => {
    const recommendations = new Map<string, PlanningRecommendation>();
    lineupLoadWarningsByCell.forEach((warning) => {
      const assignments = [...warning.assignments].sort((left, right) => left.matchDate.localeCompare(right.matchDate) || left.id.localeCompare(right.id));
      const stateSignature = assignments.map((assignment) => `${assignment.id}:${assignment.matchDate}`).join(',');
      const key = `planning:lineup_load:${warning.playerId}:state:${encodeURIComponent(stateSignature)}`;
      if (recommendations.has(key)) return;
      const player = playerById.get(warning.playerId);
      const playerName = formatPlayerName(player, userPreferences) || 'A player';
      const dates = assignments.map((assignment) => formatDate(assignment.matchDate)).filter((date, index, values) => values.indexOf(date) === index).join(', ');
      const firstAssignment = assignments[0];
      recommendations.set(key, {
        key,
        baseKey: `planning:lineup_load:${warning.playerId}`,
        recommendationType: 'lineup_load',
        teamId: firstAssignment.teamId,
        seasonId: 'multi-team',
        playerId: warning.playerId,
        stateSignature,
        message: `${playerName} is assigned to ${assignments.length} lineups within a three-day span (${dates}).`,
        priority: assignments.length >= 3 ? 'high' : 'medium',
        impact: assignments.length,
      });
    });
    return [...recommendations.values()];
  }, [lineupLoadWarningsByCell, playerById, userPreferences]);

  const planningIntelligence = useMemo(() => buildPlanningIntelligence({
    today,
    teams: teams.map((team) => ({
      id: team.id,
      seasonId: team.id,
      teamName: team.team_name,
      enforceMaxSit: team.enforce_max_sit === true,
      maxWeeksWithoutMatch: team.max_weeks_without_match || 2,
    })),
    rosters: planningRosters.map<PlanningIntelligenceRoster>((roster) => ({
      teamId: roster.team_id,
      playerId: roster.player_id,
      targetMatchCount: roster.target_match_count,
      teamPriority: roster.team_priority,
    })),
    matches: planningMatches.map<PlanningIntelligenceMatch>((match) => ({
      id: match.id,
      teamId: match.team_id,
      seasonId: match.team_id,
      matchDate: match.match_date,
      matchStatus: match.match_status,
      lineupStatus: match.lineup_status,
      opponentName: match.opponent_name,
    })),
    assignments: planningAssignments.map<PlanningIntelligenceAssignment>((entry) => ({
      matchId: entry.match_event_id,
      playerId: entry.player_id,
      positionCode: positionById.get(entry.league_configuration_position_id)?.position_code || null,
    })),
    availability: planningAvailability.map<PlanningIntelligenceAvailability>((row) => ({
      matchId: row.match_event_id,
      playerId: row.player_id,
      status: row.availability_status,
    })),
    playerNames: new Map(players.map((player) => [player.id, formatPlayerName(player, userPreferences) || 'Player'])),
    playerTolerances: new Map(players.map((player) => [player.id, player.back_to_back_match_day_tolerance])),
    playerSameDayTolerances: new Map(players.map((player) => [player.id, player.same_day_match_tolerance])),
  }), [planningAssignments, planningAvailability, planningMatches, planningRosters, players, positionById, teams, today, userPreferences]);
  const planningInsightByTeamPlayer = useMemo(
    () => new Map(planningIntelligence.insights.map((insight) => [`${insight.teamId}:${insight.playerId}`, insight])),
    [planningIntelligence.insights],
  );
  const planningDetailInsights = useMemo(
    () => planningDetailPlayerId
      ? planningIntelligence.insights.filter((insight) => insight.playerId === planningDetailPlayerId)
        .sort((left, right) => (teamById.get(left.teamId)?.team_name || '').localeCompare(teamById.get(right.teamId)?.team_name || ''))
      : [],
    [planningDetailPlayerId, planningIntelligence.insights, teamById],
  );
  const planningDetailPlayer = planningDetailPlayerId ? playerById.get(planningDetailPlayerId) : undefined;
  const participationDialogTeams = useMemo(() => teams.filter((team) => lookups.teams.some((option) => option.value === team.id)), [teams, lookups.teams]);
  const updateParticipationReports = useCallback((reports: Array<{ teamId: string; rows: ParticipationReportRow[] }>) => {
    const updated = new Map(reports.map((report) => [report.teamId, report.rows]));
    setParticipationRows((current) => [
      ...current.filter((row) => !updated.has(row.teamId)),
      ...reports.filter((report) => selectedTeamIds.includes(report.teamId)).flatMap((report) => report.rows.filter((row) => rosters.some((roster) => roster.team_id === report.teamId && roster.player_id === row.playerId)).map((row) => ({ ...row, teamId: report.teamId }))),
    ]);
  }, [selectedTeamIds, rosters]);


  const allRecommendedActions = useMemo(() => {
    const scopedPlayerIds = playerCentricView ? new Set(playerCentricRows.map((row) => row.playerId)) : null;
    return [...lineupLoadRecommendations, ...planningIntelligence.recommendations, ...buildPlanningRecommendations(boardRows.filter((row) => row.seasonId))]
      .filter((recommendation) => !scopedPlayerIds || !recommendation.playerId || scopedPlayerIds.has(recommendation.playerId))
      .sort((left, right) => {
        const priority = { high: 0, medium: 1, low: 2 } as const;
        return priority[left.priority] - priority[right.priority] || right.impact - left.impact || left.message.localeCompare(right.message);
      });
  }, [boardRows, lineupLoadRecommendations, planningIntelligence.recommendations, playerCentricRows, playerCentricView]);

  const recommendedActions = useMemo(
    () => visiblePlanningRecommendations(allRecommendedActions, dismissedRecommendationKeys),
    [allRecommendedActions, dismissedRecommendationKeys],
  );

  const dismissedCurrentRecommendationsCount = useMemo(
    () => allRecommendedActions.filter((action) => dismissedRecommendationKeys.has(action.key)).length,
    [allRecommendedActions, dismissedRecommendationKeys],
  );

  const dismissRecommendation = async (recommendation: PlanningRecommendation) => {
    if (!currentUserId) {
      setError('Your CourtCaptain user profile is required to dismiss planning recommendations.');
      return;
    }

    setDismissedRecommendationKeys((current) => new Set([...current, recommendation.key]));
    const payload = {
      notification_key: recommendation.key,
      notification_type: `planning_${recommendation.recommendationType}`,
      dismissed_at: new Date().toISOString(),
      dismissed_by: currentUserId,
    };
    const { error: dismissError } = await supabase
      .from('notification_dismissals')
      .upsert(payload, { onConflict: 'notification_key,dismissed_by' });

    if (!dismissError) return;

    setDismissedRecommendationKeys((current) => {
      const next = new Set(current);
      next.delete(recommendation.key);
      return next;
    });
    setError(dismissError.message || 'Unable to dismiss planning recommendation.');
  };

  const resetDismissedRecommendations = async () => {
    if (!currentUserId) {
      setError('Your CourtCaptain user profile is required to reset planning recommendations.');
      return;
    }
    const keys = allRecommendedActions.map((action) => action.key).filter((key) => dismissedRecommendationKeys.has(key));
    if (keys.length === 0) return;

    const { error: resetError } = await supabase
      .from('notification_dismissals')
      .delete()
      .eq('dismissed_by', currentUserId)
      .in('notification_key', keys);

    if (resetError) {
      setError(resetError.message || 'Unable to reset dismissed planning recommendations.');
      return;
    }

    setDismissedRecommendationKeys((current) => {
      const next = new Set(current);
      keys.forEach((key) => next.delete(key));
      return next;
    });
  };

  const renderSummaryCell = (row: BoardPlayerRow, column: SummaryColumnKey) => {
    if (column === 'played') {
      return (
        <Tooltip title={`${row.countedPlayed} allocation-counted played; ${row.totalPlayed} actual appearance${row.totalPlayed === 1 ? '' : 's'} (${row.singlesPlayed} singles / ${row.doublesPlayed} doubles)${row.provisionalPlayed > 0 ? `; ${row.provisionalPlayed} provisional until participants are verified` : ''}`} arrow>
          <Box component="span">{row.totalPlayed}</Box>
        </Tooltip>
      );
    }
    if (column === 'future') {
      return (
        <Tooltip title={`${row.futureSingles + row.futureDoubles} scheduled: ${row.futureSingles} singles / ${row.futureDoubles} doubles`} arrow>
          <Box component="span">{row.futureSingles + row.futureDoubles}</Box>
        </Tooltip>
      );
    }
    if (column === 'lateCancels') {
      return (
        <Tooltip title="Late cancellation events, regardless of allocation treatment" arrow>
          <Box component="span">{row.lateCancels}</Box>
        </Tooltip>
      );
    }
    if (column === 'lateSubs') {
      return (
        <Tooltip title="Late substitute and replacement appearances, regardless of allocation treatment" arrow>
          <Box component="span">{row.lateSubstitutes}</Box>
        </Tooltip>
      );
    }
    return <Typography color={`${statusColor(row.statusDelta)}.main`} sx={{ fontWeight: 700 }}>{statusLabel(row.statusDelta)}</Typography>;
  };

  return (
    <Stack spacing={1.25}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Planning Board</Typography>
          <Typography variant="body2" color="text.secondary">
            {playerCentricView ? 'Focus teams choose the players. Their matches across all your teams appear below.' : 'Multi-team availability, assignments, attention, and conflicts.'}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            aria-label={headerOpen ? 'Hide Planning Board header' : 'Show Planning Board header'}
            aria-expanded={headerOpen}
            endIcon={headerOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            onClick={() => setHeaderOpen((current) => !current)}
          >
            Header
          </Button>
          <Button
            variant="outlined"
            startIcon={isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            onClick={() => onFullScreenChange?.(!isFullScreen)}
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          >
            {isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              void loadBoard();
              void loadAvailabilityAttention();
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <ToggleButtonGroup exclusive size="small" value={playerCentricView ? 'players' : 'teams'} onChange={(_, value) => { if (value) { setPlayerViewMode(value === 'players' ? 'matrix' : 'all'); setSelectedPlayerId(''); } }} aria-label="Planning board view">
          <ToggleButton value="teams">Team View</ToggleButton>
          <ToggleButton value="players">Player View</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" type="date" label="From" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField size="small" type="date" label="Through" value={dateTo} onChange={(event) => setDateTo(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {groupMeRefreshWarning && <Alert severity="warning">{groupMeRefreshWarning}</Alert>}
      {defaultCaptainTeamIds.length === 0 && selectedTeamIds.length === 0 && <Alert severity="info">You aren't assigned as captain or co-captain of a team. Select a team to plan for.</Alert>}

      {headerOpen && (
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(300px, 1fr) minmax(210px, 0.6fr) auto' }, gap: 1.25, alignItems: 'start' }}>
            <SeasonTeamMultiSelect
              lookups={lookups}
              label="Teams"
              size="small"
              renderSelected={playerCentricView ? (ids) => <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>{ids.map((id) => <Chip key={id} size="small" variant="outlined" label={teamById.get(id)?.short_name || teamById.get(id)?.team_name || 'Team'} icon={<Box component="span" sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: teamAccentColor(teamById.get(id)?.team_color), ml: 0.75 }} />} />)}</Stack> : undefined}
              value={selectedTeamIds}
              onChange={(ids) => { if (ids.length > 4) { setError('Select up to four concurrent teams.'); return; } setTeamIds(ids); setTeamScopeMode('explicit'); }}
              teams={teams.map((team) => ({
                value: team.id,
                label: team.team_name,
                season_label: team.season_label,
              }))}
            />
            <FormControl size="small" fullWidth disabled={selectedTeamIds.length === 0}>
              <InputLabel>Player</InputLabel>
              <Select
                label="Player"
                value={playerViewMode === 'all' || playerViewMode === 'matrix' ? '' : playerViewMode === 'shared' ? '__shared__' : selectedPlayerId}
                onChange={(event) => {
                  const value = String(event.target.value);
                  if (value === '') {
                    setPlayerViewMode(playerCentricView ? 'matrix' : 'all');
                    setSelectedPlayerId('');
                  } else if (value === '__shared__') {
                    setPlayerViewMode('shared');
                    setSelectedPlayerId('');
                  } else {
                    setPlayerViewMode('player');
                    setSelectedPlayerId(value);
                  }
                }}
                aria-describedby="planning-board-player-filter-help"
              >
                <MenuItem value="">All Players</MenuItem>
                <MenuItem value="__shared__" disabled={selectedTeamIds.length < 2}>Shared Players</MenuItem>
                {selectablePlayers.map((player) => (
                  <MenuItem key={player.id} value={player.id}>{formatPlayerName(player, userPreferences)}</MenuItem>
                ))}
              </Select>
              {selectedTeamIds.length === 0 && <FormHelperText id="planning-board-player-filter-help">Select a team to filter by player.</FormHelperText>}
              {selectedTeamIds.length > 1 && playerViewMode === 'shared' && sharedPlayerIds.length === 0 && <FormHelperText id="planning-board-player-filter-help">No shared active-roster players are on every selected team.</FormHelperText>}
              {selectedTeamIds.length > 0 && selectablePlayers.length === 0 && <FormHelperText id="planning-board-player-filter-help">No active-roster players are available for the selected teams.</FormHelperText>}
            </FormControl>
            <Stack spacing={1} alignItems={{ xs: 'stretch', xl: 'flex-end' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>Options</InputLabel>
                  <Select
                    multiple
                    label="Options"
                    value={selectedPlanningOptions}
                    onChange={handlePlanningOptionsChange}
                    renderValue={(selected) => selected.length > 0
                      ? selected.map((value) => planningOptionLabels[value]).join(', ')
                      : 'Standard View'}
                  >
                    {planningOptionOrder.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={selectedPlanningOptions.includes(option)} />
                        <Typography>{planningOptionLabels[option]}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Sort</InputLabel>
                  <Select
                    label="Sort"
                    value={sortField}
                    onChange={(event) => setSortField(event.target.value as SortField)}
                  >
                    <MenuItem value="playerName">Player</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 132 }}>
                  <InputLabel>Direction</InputLabel>
                  <Select
                    label="Direction"
                    value={sortDirection}
                    onChange={(event) => setSortDirection(event.target.value as SortDirection)}
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>
                <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} showGroupBySex={false} variant="inline" />
              </Stack>
            </Stack>
          </Box>
        </Paper>
      )}

      {headerOpen && <Stack spacing={0.75}>
        {(availabilityChanges.length > 0 || availabilityChangesLoading || availabilityChangesError) && (
          <Paper variant="outlined" sx={{ width: '100%', minWidth: 0, borderLeft: '5px solid #ed6c02' }}>
            <Button
              fullWidth
              color="inherit"
              onClick={() => setAvailabilityChangesOpen((current) => !current)}
              endIcon={availabilityChangesOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              sx={{
                justifyContent: 'flex-start',
                px: 1.25,
                py: 0.75,
                textAlign: 'left',
                '& .MuiButton-endIcon': { ml: 'auto' },
              }}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography component="span" variant="subtitle2" sx={{ fontWeight: 900 }}>Recent Availability Changes</Typography>
                <Chip size="small" color={availabilityChanges.length > 0 ? 'warning' : 'default'} label={availabilityChanges.length} />
              </Box>
            </Button>
            {availabilityChangesOpen && (
              <Box sx={{ px: 1.25, pb: 1.25 }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => void loadAvailabilityAttention()} disabled={availabilityChangesLoading}>
                    Refresh
                  </Button>
                  <Button size="small" variant="contained" color="warning" onClick={() => void clearReviewedChanges()} disabled={availabilityChangesLoading || availabilityChanges.length === 0}>
                    Clear Reviewed Changes
                  </Button>
                </Stack>
                {availabilityChangesLoading && <LinearProgress sx={{ my: 1 }} />}
                {availabilityChangesError && <Alert severity="error" sx={{ mb: 1 }}>{availabilityChangesError}</Alert>}
                {availabilityChanges.length > 0 && (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                    {availabilityChanges.slice(0, 12).map((change) => (
                      <Chip
                        key={change.id}
                        color="warning"
                        variant="outlined"
                        label={`${change.player_name}: ${availabilityStatusReviewLabel(change.previous_status)} to ${availabilityStatusReviewLabel(change.new_status)} (${change.team_name} vs ${change.opponent_name})`}
                        onClick={() => onOpenAvailability?.({ teamId: change.team_id, matchId: change.match_event_id, returnTarget: 'planning-board' })}
                        sx={{ justifyContent: 'flex-start', maxWidth: { xs: '100%', md: 520 }, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                      />
                    ))}
                    {availabilityChanges.length > 12 && (
                      <Chip color="warning" variant="outlined" label={`+${availabilityChanges.length - 12} more changes`} />
                    )}
                  </Stack>
                )}
              </Box>
            )}
          </Paper>
        )}

        {(recommendedActions.length > 0 || dismissedCurrentRecommendationsCount > 0) && (
          <Paper variant="outlined" sx={{ width: '100%', minWidth: 0 }}>
            <Button
              fullWidth
              color="inherit"
              onClick={() => setRecommendationsOpen((current) => !current)}
              endIcon={recommendationsOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              sx={{
                justifyContent: 'flex-start',
                px: 1.25,
                py: 0.75,
                textAlign: 'left',
                '& .MuiButton-endIcon': { ml: 'auto' },
              }}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography component="span" variant="subtitle2" sx={{ fontWeight: 900 }}>Recommended Actions</Typography>
                <Chip size="small" color={recommendedActions.length > 0 ? 'warning' : 'default'} label={recommendedActions.length} />
              </Box>
            </Button>
            {recommendationsOpen && (
              <Box sx={{ px: 1.25, pb: 1.25 }}>
                {dismissedCurrentRecommendationsCount > 0 && (
                  <Button size="small" variant="outlined" onClick={() => void resetDismissedRecommendations()} sx={{ mb: 1 }}>
                    Reset dismissed
                  </Button>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  {recommendedActions.slice(0, 12).map((action) => (
                    <Chip
                      key={action.key}
                      label={action.message}
                      color="warning"
                      variant="outlined"
                      onDelete={() => void dismissRecommendation(action)}
                      deleteIcon={(
                        <Tooltip title="Dismiss recommendation" arrow>
                          <CloseIcon fontSize="small" />
                        </Tooltip>
                      )}
                      sx={{
                        justifyContent: 'flex-start',
                        maxWidth: { xs: '100%', sm: 420 },
                        '& .MuiChip-label': { whiteSpace: 'normal' },
                      }}
                    />
                  ))}
                </Stack>
                {recommendedActions.length > 12 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Showing the 12 highest-priority actions. Review individual player planning details for lower-priority context.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        )}
      </Stack>}

      {playerCentricView && <Paper variant="outlined" sx={{ px: 1.5, py: 0.85, borderRadius: 1.5 }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} alignItems="center">
          {['Planned', 'Available', 'Not Available', 'Maybe', 'No response'].map((label) => <Typography key={label} variant="caption"><Box component="span" sx={{ ...playerViewBadge(label), mr: 0.5 }}>●</Box>{label}</Typography>)}
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}><WarningAmberIcon color="error" sx={{ fontSize: 16 }} />Conflict</Typography>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}><FlagIcon color="warning" sx={{ fontSize: 16 }} />Published lineup</Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => onUserPreferencesChange({ groupPlayersBySex: !userPreferences.groupPlayersBySex })}>{userPreferences.groupPlayersBySex ? 'Group: Gender' : 'Group: None'}</Button>
          <Button size="small" onClick={() => setIncludeCompleted((current) => !current)}>{includeCompleted ? 'Hide completed matches' : 'Show completed matches'}</Button>
        </Stack>
      </Paper>}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer ref={gridScrollRef} sx={{ maxHeight: 'calc(100vh - 285px)', overflow: 'auto' }}>
          <Table size="small" stickyHeader sx={{ width: tableMinWidth, minWidth: tableMinWidth, borderCollapse: 'separate', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: accentColumnWidth }} />
              <col style={{ width: playerColumnWidth }} />
              {summaryColumns.map((column) => (
                <col key={column.key} style={{ width: summaryColumnWidth }} />
              ))}
              {matchColumns.map((column) => (
                <col key={column.key} style={{ width: matchColumnWidth }} />
              ))}
            </colgroup>
            <TableBody>
              {displayedTeamIds.map((teamId) => {
                const team = teamById.get(teamId);
                const rows = rowsByTeam.get(teamId) || [];
                return [
                  <TableRow key={`team:${teamId}`}>
                    <TableCell colSpan={fixedColumnCount + matchColumns.length} sx={{ bgcolor: '#f3f6f3', borderLeft: `6px solid ${teamAccentColor(team?.team_color || 'gray')}`, fontWeight: 800 }}>
                      {playerCentricView ? 'Cross-Team Player View' : team?.team_name || 'Team'} <Typography component="span" variant="caption" color="text.secondary">({rows.length} players)</Typography>
                    </TableCell>
                  </TableRow>,
                  <TableRow key={`team-header:${teamId}`}>
                    <TableCell sx={{ position: 'sticky', top: 0, left: 0, zIndex: 5, width: accentColumnWidth, minWidth: accentColumnWidth, maxWidth: accentColumnWidth, bgcolor: 'background.paper', p: 0 }} />
                    <TableCell sx={{ position: 'sticky', top: 0, left: playerColumnLeft, zIndex: 5, width: playerColumnWidth, minWidth: playerColumnWidth, maxWidth: playerColumnWidth, bgcolor: 'background.paper', fontWeight: 700, borderRight: summaryColumns.length === 0 ? '2px solid #c7cfd6' : '1px solid #e3e8ec' }}>Player</TableCell>
                    {summaryColumns.map((column, index) => (
                      <TableCell
                        key={column.key}
                        align="right"
                        sx={{
                          position: 'sticky',
                          top: 0,
                          left: summaryColumnLeft(index),
                          zIndex: 5,
                          width: summaryColumnWidth,
                          minWidth: summaryColumnWidth,
                          maxWidth: summaryColumnWidth,
                          bgcolor: 'background.paper',
                          fontWeight: 700,
                          borderRight: index === summaryColumns.length - 1 ? '2px solid #c7cfd6' : '1px solid #e3e8ec',
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                    {matchColumns.map((column) => {
                      const dayMatches = matchesForTeamColumn(teamId, column);
                      const hasMatches = dayMatches.length > 0;
                      return (
                        <TableCell
                          key={`${teamId}:${column.key}`}
                          align="center"
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 3,
                            width: matchColumnWidth,
                            minWidth: matchColumnWidth,
                            maxWidth: matchColumnWidth,
                            fontWeight: 700,
                            bgcolor: playerCentricView ? '#f6f8fb' : hasMatches ? 'background.paper' : '#f8f8f8',
                            borderLeft: playerCentricView ? '1px solid #e1e7ed' : hasMatches ? `5px solid ${teamAccentColor(team?.team_color || 'gray')}` : '1px solid #eeeeee',
                            color: hasMatches ? 'text.primary' : 'text.disabled',
                            p: 0.5,
                            verticalAlign: 'top',
                          }}
                        >
                          {playerCentricView && <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" fontWeight={800}>{new Date(`${column.match_date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' })}<Box component="span" sx={{ display: 'block', color: '#15232c', fontSize: '0.83rem' }}>{formatDate(column.match_date)}</Box><Box component="span" sx={{ display: 'block', fontWeight: 400, color: 'text.secondary' }}>{dayMatches.length} {dayMatches.length === 1 ? 'match' : 'matches'}</Box></Typography>
                            {playerCentricView && dayMatches.some((match) => coverageForMatch(match).warn) && (
                              <Tooltip title={<Box><Typography variant="body2" fontWeight={800}>{dayMatches.filter((match) => coverageForMatch(match).warn).reduce((count, match) => count + coverageForMatch(match).missingSlots.length, 0)} lineup spots still need players</Typography>{dayMatches.filter((match) => coverageForMatch(match).warn).map((match) => <Box key={match.id} sx={{ mt: 1 }}><Typography variant="caption">{teamById.get(match.team_id)?.team_name} · {formatTime(match.match_time, timeFormat)}</Typography><Typography variant="caption" component="div">{coverageForMatch(match).missingSlots.join(' · ')}</Typography></Box>)}</Box>} arrow><IconButton size="small" aria-label={`View incomplete lineups for ${column.match_date}`} onClick={(event) => setCoveragePopover({ anchor: event.currentTarget, date: column.match_date, matches: dayMatches })}><WarningAmberIcon color="warning" fontSize="small" /></IconButton></Tooltip>
                            )}
                          </Stack>}
                          {playerCentricView ? <Typography variant="caption" color="text.secondary">{dayMatches.filter((match) => match.match_status !== 'Completed' && coverageForMatch(match).state === 'not-started').length || ''} {dayMatches.some((match) => match.match_status !== 'Completed' && coverageForMatch(match).state === 'not-started') ? 'lineups not started' : ''}</Typography> : hasMatches ? (
                            <Stack spacing={0.5}>
                              {dayMatches.map((match, matchIndex) => (
                                <Box
                                  key={match.id}
                                  sx={{
                                    display: 'grid',
                                    gap: 0.35,
                                    width: '100%',
                                    minWidth: 0,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    py: 0.5,
                                    px: 0.5,
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    bgcolor: matchIndex > 0 ? 'rgba(15, 23, 42, 0.03)' : 'transparent',
                                  }}
                                >
                                  <Box
                                    component="button"
                                    type="button"
                                    onClick={() => openMatchDetails(match)}
                                    sx={{
                                      appearance: 'none',
                                      border: 0,
                                      bgcolor: 'transparent',
                                      color: 'inherit',
                                      cursor: 'pointer',
                                      display: 'block',
                                      font: 'inherit',
                                      m: 0,
                                      minWidth: 0,
                                      p: 0,
                                      textAlign: 'center',
                                      width: '100%',
                                      '&:hover .PlanningBoard-matchTitle': { textDecoration: 'underline' },
                                      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
                                    }}
                                  >
                                    <Typography className="PlanningBoard-matchTitle" variant="caption" component="div" sx={{ fontWeight: 900, lineHeight: 1.15 }}>{matchDateTimeLabel(match, timeFormat)}</Typography>
                                    <Typography variant="caption" component="div" color="text.secondary" noWrap sx={{ lineHeight: 1.15 }}>{match.opponent_name || 'TBD'}</Typography>
                                  </Box>
                                  <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                                    <Chip size="small" variant="outlined" label={siteTypeLabel(match.match_site_type)} sx={{ height: 18, maxWidth: 62, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />
                                    <Chip size="small" label={match.lineup_status || 'Not Started'} sx={{ height: 18, maxWidth: 72, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />
                                    {renderEdrIndicator(match)}
                                  </Stack>
                                  {renderOpenSlotsIndicator(match)}
                                  <Stack direction="row" spacing={0.25} justifyContent="center" alignItems="center">
                                    <Tooltip title="Match Details" arrow>
                                      <IconButton size="small" aria-label={`Open match details for ${match.opponent_name || 'match'}`} onClick={() => openMatchDetails(match)}>
                                        <VisibilityIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                    {onOpenAvailability && (
                                      <Tooltip title="Player Availability" arrow>
                                        <IconButton size="small" aria-label={`Open player availability for ${match.opponent_name || 'match'}`} onClick={() => openAvailabilityFromMatch(match)}>
                                          <EventAvailableIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Lineup Builder" arrow>
                                      <IconButton size="small" aria-label={`Open lineup builder for ${match.opponent_name || 'match'}`} onClick={() => openLineupDialog(match.id)}>
                                        <FormatListBulletedIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Box sx={{ minHeight: 78, display: 'grid', placeItems: 'center' }}>
                              <Typography variant="caption" component="div" color="text.disabled">{formatDate(localEventDate(column))}</Typography>
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>,
                  ...rows.flatMap((row, rowIndex) => [
                    ...(playerCentricView && userPreferences.groupPlayersBySex && (rowIndex === 0 || rows[rowIndex - 1].playerSex !== row.playerSex) ? [<TableRow key={`gender:${row.playerSex}`}><TableCell colSpan={fixedColumnCount + matchColumns.length} sx={{ py: 0.65, bgcolor: row.playerSex === 'female' ? '#fff0f2' : '#eaf3ff', fontWeight: 800 }}>{row.playerSex === 'female' ? 'Women' : row.playerSex === 'male' ? 'Men' : 'Other / unspecified'} ({rows.filter((item) => item.playerSex === row.playerSex).length})</TableCell></TableRow>] : []),
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, width: accentColumnWidth, minWidth: accentColumnWidth, maxWidth: accentColumnWidth, bgcolor: playerCentricView ? '#ffffff' : teamAccentColor(team?.team_color || 'gray'), p: 0 }} />
                      <TableCell onClick={() => onOpenPlayerSchedule(row.playerId)} sx={{ position: 'sticky', left: playerColumnLeft, zIndex: 2, bgcolor: 'background.paper', width: playerColumnWidth, minWidth: playerColumnWidth, maxWidth: playerColumnWidth, cursor: 'pointer', fontWeight: 600, verticalAlign: playerCentricView ? 'top' : undefined, pt: playerCentricView ? 1.25 : undefined, borderRight: summaryColumns.length === 0 ? '2px solid #c7cfd6' : '1px solid #e3e8ec' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.playerName}</Box>
                          <Tooltip title="View planning details" arrow>
                            <IconButton
                              size="small"
                              aria-label={`View planning details for ${row.playerName}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                setPlanningDetailPlayerId(row.playerId);
                              }}
                              sx={{ p: 0.25, flex: '0 0 auto' }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {playerCentricView && <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.6} sx={{ mt: 0.75 }}>
                          {boardRows.filter((teamRow) => teamRow.playerId === row.playerId).map((teamRow) => <Tooltip key={teamRow.teamId} title={<Box sx={{ whiteSpace: 'pre-line' }}>{`${teamById.get(teamRow.teamId)?.team_name || 'Team'}\nPlayed: ${teamRow.totalPlayed}\nFuture planned: ${teamRow.futureSingles + teamRow.futureDoubles}\nLate cancellations: ${teamRow.lateCancels}\nLate substitutions: ${teamRow.lateSubstitutes}\nParticipation target: ${teamRow.participationTarget ?? 0}\nDeviation: ${statusLabel(teamRow.statusDelta)}`}</Box>} arrow><Box component="button" type="button" aria-label={`${teamById.get(teamRow.teamId)?.short_name || teamById.get(teamRow.teamId)?.team_name || 'Team'} — ${teamRow.statusDelta < 0 ? 'Low' : teamRow.statusDelta > 0 ? 'High' : 'On Target'}`} onClick={(event) => { event.stopPropagation(); setParticipationPopover({ anchor: event.currentTarget, row: teamRow }); }} sx={{ border: `1px solid ${teamAccentColor(teamById.get(teamRow.teamId)?.team_color)}`, borderRadius: 1, px: 0.75, py: 0.4, bgcolor: '#fff', backgroundImage: `linear-gradient(${teamAccentColor(teamById.get(teamRow.teamId)?.team_color)}12, ${teamAccentColor(teamById.get(teamRow.teamId)?.team_color)}12)`, cursor: 'pointer', textAlign: 'left', maxWidth: 118, color: '#25313a', '&:focus-visible': { outline: '2px solid #1976d2', outlineOffset: 2 } }}>
                            <Typography component="div" variant="caption" noWrap sx={{ fontSize: '0.68rem', lineHeight: 1.25 }}><Box component="span" sx={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', mr: 0.4, bgcolor: teamAccentColor(teamById.get(teamRow.teamId)?.team_color) }} />{teamById.get(teamRow.teamId)?.short_name || teamById.get(teamRow.teamId)?.team_name || 'Team'}</Typography>
                            <Box component="span" sx={playerViewBadge(teamRow.statusDelta === 0 ? 'Planned' : teamRow.statusDelta < 0 ? 'Available' : 'Maybe')}>{teamRow.statusDelta < 0 ? 'Low' : teamRow.statusDelta > 0 ? 'High' : 'On Target'}</Box>
                          </Box></Tooltip>)}
                        </Stack>}
                        {!playerCentricView && sexInitial(row.playerSex) && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5, fontWeight: 700 }}>
                            ({sexInitial(row.playerSex)})
                          </Typography>
                        )}
                        {(() => {
                          const insight = planningInsightByTeamPlayer.get(`${row.teamId}:${row.playerId}`);
                          if (!insight || (insight.pressure === 'normal' && insight.teamPriority === 'normal')) return null;
                          const label = insight.pressure === 'normal'
                            ? `${insight.teamPriority} priority`
                            : insight.pressure.replace(/_/g, ' ');
                          return <Chip size="small" color={insight.pressure === 'unreachable' || insight.pressure === 'at_risk' ? 'error' : 'warning'} label={label} sx={{ ml: 0.5, height: 18, '& .MuiChip-label': { px: 0.55, fontSize: '0.62rem', fontWeight: 800 } }} />;
                        })()}
                        <Box sx={{ display: playerCentricView ? 'none' : { xs: 'grid', md: 'none' }, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.5, mt: 0.75 }}>
                          <Typography variant="caption" color="text.secondary">Played: {row.totalPlayed}</Typography>
                          <Typography variant="caption" color="text.secondary">Future: {row.futureSingles + row.futureDoubles}</Typography>
                          <Typography variant="caption" color="text.secondary">Late Cancels: {row.lateCancels}</Typography>
                          <Typography variant="caption" color="text.secondary">Late Subs: {row.lateSubstitutes}</Typography>
                          <Typography variant="caption" color={`${statusColor(row.statusDelta)}.main`}>Status: {statusLabel(row.statusDelta)}</Typography>
                        </Box>
                      </TableCell>
                      {summaryColumns.map((column, index) => (
                        <TableCell
                          key={column.key}
                          align="right"
                          sx={{
                            position: 'sticky',
                            left: summaryColumnLeft(index),
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            width: summaryColumnWidth,
                            minWidth: summaryColumnWidth,
                            maxWidth: summaryColumnWidth,
                            borderRight: index === summaryColumns.length - 1 ? '2px solid #c7cfd6' : '1px solid #e3e8ec',
                          }}
                        >
                          {renderSummaryCell(row, column.key)}
                        </TableCell>
                      ))}
                      {matchColumns.map((column) => {
                        const dayMatches = matchesForTeamColumn(teamId, column);
                        const dayCells = dayMatches.map((match) => ({ match, cell: cellFor(row, match) }));
                        const assignedCells = dayCells.filter(({ cell }) => Boolean(cell.text) && cell.availability);
                        const primaryCell = assignedCells[0]?.cell || dayCells[0]?.cell;
                        const style = primaryCell?.availability ? availabilityStyles[primaryCell.availability] : undefined;
                        const tooltip = dayCells
                          .map(({ match, cell }) => cell.tooltip ? `${matchShortLabel(match, timeFormat)}\n${cell.tooltip}` : '')
                          .filter(Boolean)
                          .join('\n\n');
                        return (
                          <Tooltip key={`${row.id}:${column.key}`} title={tooltip ? <Box sx={{ whiteSpace: 'pre-line' }}>{tooltip}</Box> : ''} arrow>
                            <TableCell
                              align="center"
                              sx={{
                                width: matchColumnWidth,
                                minWidth: matchColumnWidth,
                                maxWidth: matchColumnWidth,
                                height: 46,
                                bgcolor: playerCentricView ? 'background.paper' : dayMatches.length > 0 ? style?.bg || '#eeeeee' : '#f8f8f8',
                                color: dayMatches.length > 0 ? style?.text || 'text.secondary' : 'text.disabled',
                                border: playerCentricView ? '1px solid #e6ebf0' : dayMatches.length > 0 ? `1px solid ${style?.border || '#dddddd'}` : '1px solid #eeeeee',
                                verticalAlign: 'top',
                                p: 0.5,
                              }}
                            >
                              {dayMatches.length > 0 ? (
                                <Stack spacing={0.5}>
                                  {playerCentricView && dayCells.every(({ cell }) => !cell.availability) && <Typography variant="caption" color="text.disabled" sx={{ py: 2 }}>—</Typography>}
                                  {dayCells.filter(({ cell }) => !playerCentricView || cell.availability !== undefined).map(({ match, cell }) => (
                                    <Box
                                      key={match.id}
                                      component={playerCentricView ? 'button' : 'div'}
                                      type={playerCentricView ? 'button' : undefined}
                                      onClick={playerCentricView ? (event: React.MouseEvent<HTMLElement>) => setMatchPopover({ anchor: event.currentTarget, match, details: cell.tooltip }) : undefined}
                                      sx={{
                                        width: '100%',
                                        cursor: playerCentricView ? 'pointer' : undefined,
                                        border: playerCentricView ? `1px solid ${teamAccentColor(teamById.get(match.team_id)?.team_color)}` : 0,
                                        color: '#17212b',
                                        font: 'inherit',
                                        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                                        alignItems: 'center',
                                        borderRadius: 0.75,
                                        display: 'grid',
                                        minHeight: playerCentricView ? 66 : dayMatches.length > 1 ? 34 : 38,
                                        px: playerCentricView ? 0.85 : 0.35,
                                        py: playerCentricView ? 0.65 : 0.25,
                                        position: 'relative',
                                        backgroundImage: playerCentricView ? `linear-gradient(${teamAccentColor(teamById.get(match.team_id)?.team_color)}12, ${teamAccentColor(teamById.get(match.team_id)?.team_color)}12)` : undefined,
                                        bgcolor: playerCentricView ? '#ffffff' : cell.availability ? 'rgba(255, 255, 255, 0.58)' : 'transparent',
                                      }}
                                    >
                                      {cell.conflict && <CellCornerIndicator corner="left" color="#d32f2f" label={`Scheduling conflict: ${cell.conflict}`} />}
                                      {cell.attention && (
                                        <CellCornerIndicator
                                          corner="right"
                                          color="#ed6c02"
                                          label={`Changed from ${availabilityStatusReviewLabel(cell.attention.previous_status)} to ${availabilityStatusReviewLabel(cell.attention.new_status)}`}
                                        />
                                      )}
                                      {cell.lineupLoadWarning && (
                                        <Tooltip title={`${cell.lineupLoadWarning.assignments.length} lineup assignments within a three-day span`} arrow>
                                          <Box
                                            component="span"
                                            role="img"
                                            aria-label={`${cell.lineupLoadWarning.assignments.length} lineup assignments within a three-day span`}
                                            sx={{ position: 'absolute', right: 1, bottom: 0, color: 'warning.main', display: 'inline-flex', lineHeight: 1 }}
                                          >
                                            <FlagIcon sx={{ fontSize: 16 }} />
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {cell.maybeAssignment && (
                                        <Tooltip title="Assigned while marked Maybe" arrow>
                                          <WarningAmberIcon
                                            aria-label="Assigned while marked Maybe"
                                            sx={{ position: 'absolute', left: 1, bottom: 0, color: 'warning.main', fontSize: 16 }}
                                          />
                                        </Tooltip>
                                      )}
                                      <Box sx={{ minWidth: 0, textAlign: playerCentricView ? 'left' : 'center' }}>
                                        {(dayMatches.length > 1 || playerCentricView) && (
                                          <Typography variant="caption" component="div" noWrap sx={{ fontSize: '0.7rem', lineHeight: 1.3, opacity: 0.9 }}>
                                            {playerCentricView ? `${teamById.get(match.team_id)?.short_name || teamById.get(match.team_id)?.team_name || 'Team'}` : matchShortLabel(match, timeFormat)}
                                          </Typography>
                                        )}
                                        {playerCentricView && <Typography variant="caption" component="div" sx={{ lineHeight: 1.4 }}>{formatTime(match.match_time, timeFormat) || 'Time TBD'}</Typography>}
                                        <Typography variant="caption" component="div" sx={playerCentricView ? playerViewBadge(playerViewStatus(cell.availability, (entriesByMatchPlayer.get(`${match.id}:${row.playerId}`)?.length || 0) > 0)) : { fontWeight: 800, lineHeight: 1.1 }}>{playerCentricView ? playerViewStatus(cell.availability, (entriesByMatchPlayer.get(`${match.id}:${row.playerId}`)?.length || 0) > 0) : cell.text || '—'}</Typography>
                                        {playerCentricView && match.lineup_status === 'Published' && (entriesByMatchPlayer.get(`${match.id}:${row.playerId}`)?.length || 0) > 0 && <Tooltip title="Published lineup"><FlagIcon aria-label="Published lineup" sx={{ position: 'absolute', right: 4, top: 4, fontSize: 15, color: '#d36705' }} /></Tooltip>}
                                      </Box>
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="caption" sx={{ fontWeight: 700 }} />
                              )}
                            </TableCell>
                          </Tooltip>
                        );
                      })}
                    </TableRow>
                  ]),
                ];
              })}
              {displayedBoardRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={fixedColumnCount + matchColumns.length}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
                      No planning board rows match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Popover open={Boolean(matchPopover)} anchorEl={matchPopover?.anchor} onClose={() => setMatchPopover(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {matchPopover && <Stack spacing={1} sx={{ p: 2, maxWidth: 380 }}>
          <Typography fontWeight={800} sx={{ borderLeft: `5px solid ${teamAccentColor(teamById.get(matchPopover.match.team_id)?.team_color)}`, pl: 1 }}>{teamById.get(matchPopover.match.team_id)?.team_name} vs {matchPopover.match.opponent_name || 'TBD'}</Typography>
          <Typography variant="body2">{formatDate(localEventDate(matchPopover.match))} · {formatTime(matchPopover.match.match_time, timeFormat) || 'Time TBD'} · {siteTypeLabel(matchPopover.match.match_site_type)}</Typography>
          <Typography variant="body2">{matchPopover.match.lineup_status || 'Not Started'} · {coverageForMatch(matchPopover.match).filled} filled · {coverageForMatch(matchPopover.match).missingSlots.length} unfilled slots</Typography>
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>{matchPopover.details}</Typography>
          <Button onClick={() => { openLineupDialog(matchPopover.match.id); setMatchPopover(null); }}>Open in Lineup Builder</Button>
          {onOpenAvailability && <Button onClick={() => { openAvailabilityFromMatch(matchPopover.match); setMatchPopover(null); }}>View Player Availability</Button>}
          {onOpenMatchDetail && <Button onClick={() => { openMatchDetails(matchPopover.match); setMatchPopover(null); }}>View Match Event Details</Button>}
        </Stack>}
      </Popover>
      <Popover open={Boolean(coveragePopover)} anchorEl={coveragePopover?.anchor} onClose={() => setCoveragePopover(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {coveragePopover && <Stack spacing={1} sx={{ p: 2, maxWidth: 380 }}>
          <Typography fontWeight={800}>{coveragePopover.matches.filter((match) => coverageForMatch(match).warn).reduce((count, match) => count + openSlotsIndicatorForMatch(match).missingSlots.length, 0)} lineup spots still need players</Typography>
          <Typography>{formatDate(coveragePopover.date)}</Typography>
          {coveragePopover.matches.filter((match) => coverageForMatch(match).warn).map((match) => <Box key={match.id}>
            <Button onClick={() => { openLineupDialog(match.id); setCoveragePopover(null); }}>{teamById.get(match.team_id)?.team_name} · {formatTime(match.match_time, timeFormat)}</Button>
            {[...new Set(openSlotsIndicatorForMatch(match).missingSlots)].map((line) => <Typography key={line} variant="body2">{line}: {openSlotsIndicatorForMatch(match).missingSlots.filter((slot) => slot === line).length} player(s) needed</Typography>)}
          </Box>)}
        </Stack>}
      </Popover>
      <Popover open={Boolean(participationPopover)} anchorEl={participationPopover?.anchor} onClose={() => setParticipationPopover(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {participationPopover && <Stack spacing={0.5} sx={{ p: 2 }}>
          <Typography fontWeight={800}>{teamById.get(participationPopover.row.teamId)?.team_name}</Typography>
          <Typography variant="body2">Played: {participationPopover.row.totalPlayed}</Typography>
          <Typography variant="body2">Future planned: {participationPopover.row.futureSingles + participationPopover.row.futureDoubles}</Typography>
          <Typography variant="body2">Late cancellations: {participationPopover.row.lateCancels}</Typography>
          <Typography variant="body2">Late substitutions: {participationPopover.row.lateSubstitutes}</Typography>
          <Typography variant="body2">Participation target: {participationPopover.row.participationTarget ?? 0}</Typography>
          <Typography variant="body2">Participation deviation: {statusLabel(participationPopover.row.statusDelta)}</Typography>
          <Button onClick={() => { setPlanningDetailPlayerId(participationPopover.row.playerId); setParticipationPopover(null); }}>View participation details</Button>
        </Stack>}
      </Popover>
      <PlayerParticipationDetailsDialog
        key={planningDetailPlayerId || 'closed'}
        playerId={planningDetailPlayerId}
        playerName={formatPlayerName(planningDetailPlayer, userPreferences) || 'Player'}
        teams={participationDialogTeams}
        userPreferences={userPreferences}
        timeFormat={timeFormat}
        insights={planningDetailInsights}
        onClose={() => setPlanningDetailPlayerId(null)}
        onReportsLoaded={updateParticipationReports}
        onOpenLineup={(match) => {
          setPlanningDetailPlayerId(null);
          if (onOpenLineup) onOpenLineup(match.id, { teamId: match.team_id });
          else setLineupDialogMatchId(match.id);
        }}
      />

      <Dialog open={Boolean(lineupDialogMatchId)} onClose={() => void closeLineupDialog()} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pr: 1 }}>
          Lineup Builder
          <IconButton aria-label="Close lineup builder" onClick={() => void closeLineupDialog()}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
          <LineupBuilder
            matchId={lineupDialogMatchId}
            lookups={lookups}
            onBackToMatch={() => void closeLineupDialog()}
            userPreferences={userPreferences}
            onUserPreferencesChange={onUserPreferencesChange}
            timeFormat={timeFormat}
          />
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
